/**
 * 语文听写的**工作区**：一套界面，两种模式。
 *
 * | 模式 | 谁用 | 装什么 | 后端路径 |
 * | --- | --- | --- | --- |
 * | `maintain` | 系统管理员（`/admin/dictation`） | 浏览**与编辑**、按书录入（含**添加出版社**：建一册）；整块导入**默认隐藏**（`DICTATION_WHOLE_IMPORT_UI_ENABLED=false`，端点与组件都还在） | `/admin/dictation/*` |
 * | `browse` | 家长（`/tools/dictation`） | 按书**看**字表、浏览字词、选词预览 | `/students/:id/dictation/*` |
 *
 * ## 为什么是"一个组件两种模式"，而不是两个页面
 *
 * 2026-10-16 用户判断：字表的**维护**是站点的事（"每个家庭都要录一遍这个字，
 * 那岂不是太浪费时间，而且没有办法资源重用吗？所以我觉得录入维护这一块
 * 应该由系统管理员去做"）。**但"看字表 / 选词 / 布置听写"仍然是家长的事** ——
 * 两边是**同一份数据、同一套面板**，只有"能不能写"和"打到哪条后端路径"不同。
 *
 * 所以这里只分两种模式，**面板与查询逻辑一行都不重复**：
 *   · 数据请求统一走 `scope`（`dictationBase()` 决定前缀）；
 *   · 写操作的入口（弹窗、导入卡片、编辑按钮）只在 `maintain` 下**渲染**。
 *
 * ⛔ 不要为了省事把 `mode` 做成"按钮灰掉"：本项目明确反对"点了会失败"的按钮。
 *   家长模式下那些入口是**不存在**的（连端点都不存在）。
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DICTATION_BROWSE_LIMIT_MAX,
  ERROR_CODES,
  describeTextbookLabel,
  splitReadingsByHidden,
} from '@fht/shared';
import { Card, EmptyState, Skeleton } from '../ui/Card';import { Button } from '../ui/Button';
import { useToast } from '../ui/Toast';
import { ErrorState } from '../ui/Bits';
import { ApiError, toErrorMessage } from '../../lib/api/error';
import * as dictationApi from '../../lib/api/dictation';
import * as adminApi from '../../lib/api/dictationAdmin';
import * as sessionApi from '../../lib/api/dictationSessions';
import {
  buildCreateTextbookPayload,
  describeNewTextbookAlreadyExists,
  describeNewTextbookCreated,
  findExistingTextbook,
  type NewTextbookInput,
} from '../../lib/dictationTextbook';
import {
  DEFAULT_BOOKS_GRADE,
  DEFAULT_BOOKS_SEMESTER,
  describeSelectedVolume,
  findPublisherVolume,
  publisherLabelOf,
  resolveBooksImportTarget,
  selectionOfVolume,
} from '../../lib/dictationBooksPicker';
import {
  buildCreateHanziPayload,
  buildCreateLessonPayload,
  buildCreateWordPayload,
  buildImportRequest,
  buildListImportRequest,
  buildUpdateHanziPayload,
  buildUpdateLessonPayload,
  buildUpdateWordPayload,
} from '../../lib/dictationWrite';
import {
  commitFailed,
  commitSucceeded,
  initialImportState,
  previewFailed,
  previewSucceeded,
  resetImport,
  restoreLastCommittedText,
  setImportInput,
  startCommit,
  startPreview,
  validateImportInput,
} from '../../lib/dictationImport';
import type { ImportState } from '../../lib/dictationImport';
import {
  confirmedReadingsPayload,
  rememberConfirmedReading,
  syncConfirmedReadings,
} from '../../lib/importConfirmedReadings';
import type { ImportIssueRow } from '../../lib/importIssues';
import { lessonsUiEnabled } from '../../lib/dictationLessonsUi';
import { purgeLegacyHiddenReadings, toggleHiddenReading } from '../../lib/hanziReadingPrefs';
import {
  defaultDictationTab,
  dictationTabs,
  wholeImportUiEnabled,
  type DictationTabKey,
} from '../../lib/dictationTabs';
import {
  browseBlocks,
  browseListsEnabled,
  browseQueryDecision,
  clearBrowseFilterFields,
  describeDroppedFilterValues,
  EMPTY_BROWSE_FILTER,
  findFilterVolume,
  isFilterActive,
  publisherFilterOptions,
  readBrowseFilter,
  reconcileBrowseFilter,
  writeBrowseFilter,
  type BrowseFilterState,
} from '../../lib/dictationFilters';
import { DictationImportPanel } from './DictationImportPanel';
import { DictationBooksPanel } from './DictationBooksPanel';
import { DictationNewTextbookForm } from './DictationNewTextbookForm';
import { HanziReadingsModal } from './HanziReadingsModal';
/** 一次造多个词（2026-10-30）。 */
import { WordBatchCreateModal } from './WordBatchCreateModal';
import { WordCandidatePicker } from './WordCandidatePicker';
import { DictationItemsPanel } from './DictationItemsPanel';
import { DictationPickPanel } from './DictationPickPanel';
import { DictationItemForm, type ItemFormSubmit, type ItemKind } from './DictationItemForm';
import { DictationLessonForm, type LessonFormSubmit } from './DictationLessonForm';
import { DeleteConfirmModal, type DeleteTarget } from './DictationDialogs';
import { browseJumpCards, hanziRowKey, useHanziJump } from './hanziJump';
import type {
  DictationScope,
  HanziView,
  ImportReportView,
  LessonView,
  TextbookView,
  WordView,
} from '../../lib/api/dictation';

/** `maintain` = 能改（管理员）；`browse` = 只能看（家长）。 */
export type DictationWorkspaceMode = 'maintain' | 'browse';

/**
 * 当前 tab 的 key（清单与顺序在 `lib/dictationTabs.ts`，那里有单测）。
 *
 * ⚠️ 默认页是 `DEFAULT_DICTATION_TAB` = **按书录入**（`books`）：用户要求
 *    "调换标签顺序，默认选中保持不变" —— 顺序与默认页是两件事，别混在一起改。
 */
type Tab = DictationTabKey;

/**
 * 筛选状态 = `BrowseFilterState`（字段口径、判据、本机记忆全在 `lib/dictationFilters.ts`）。
 *
 * ⚠️ 初值**不是**"全空"，而是"本机记住的那一份"（见下面 `restored`）——
 *    用户原话："每次重选很烦"。
 * ⚠️ 这里不再有 `EMPTY_FILTER` 常量：默认值只有一个来源（`EMPTY_BROWSE_FILTER`），
 *    两处各写一份迟早会漏字段。
 */
type FilterState = BrowseFilterState;

/**
 * 「按书 + 表」的界面状态（第五步的主视角）。
 *
 * 与 `filter` 分开：那一份是"浏览与编辑"用的筛选，这一份是"我正在看/录哪本书的哪张表"。
 * 两者混在一起会让"切到浏览页再切回来"把正在录的那张表弄丢。
 *
 * ⚠️ 2026-10-18 本轮：**不再存 `textbookId`**。真相是
 *    **出版社 + 年级 + 学期** 三格一起定位一册（`findPublisherVolume()`）：
 *    · 三格有对应册 → 就是那一册；
 *    · **书还没建过 → 照常能录**：三张表按 0 显示，预览 / 导入照常发，
 *      保存时后端按这三格**自动建出那本书**并把字表归到它下面。
 *    年级 / 学期是**预设值**（`GRADES` + 上 / 下 / 不分），没有"新建"入口。
 */
interface BooksSelection {
  /** 出版社下拉的 value（`publisherOptionValue()` 算出来的稳定键）。 */
  publisher: string;
  gradeCode: string;
  semester: string;
  listType: 'shizi' | 'xiezi' | 'cibiao';
}

const EMPTY_BOOKS_SELECTION: BooksSelection = {
  publisher: '',
  /**
   * ⚠️ 年级 / 学期**始终可选**，默认 小学一年级 / 上册：
   *    它们是"要建 / 要看哪一册"的依据（三格一起定位）。
   */
  gradeCode: DEFAULT_BOOKS_GRADE,
  semester: DEFAULT_BOOKS_SEMESTER,
  listType: 'shizi',
};

/**
 * 「添加出版社」提交之后的结果 —— **对用户来说只有一件好事：那一册被选中了**。
 *
 * `created: true` = 这次真的建了一本；`false` = 已经有过了（唯一键撞上、
 * 或者提交前就在列表里找到），两种情况都会把 `textbook` 选中，
 * 只有提示语不同（见 `describeNewTextbookCreated` / `...AlreadyExists`）。
 */
interface NewTextbookOutcome {
  created: boolean;
  textbook: TextbookView;
  input: NewTextbookInput;
}

/** 只保留非空查询参数（空串对后端没有意义，而且会让查询键不稳定）。 */
function cleanQuery(query: Record<string, string | undefined>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') out[key] = value;
  }
  return out;
}

export function DictationWorkspace({
  scope,
  mode,
  studentGradeCode = '',
}: {
  scope: DictationScope;
  mode: DictationWorkspaceMode;
  /**
   * 选词预览默认选中的年级。
   *
   * 家长模式下来自**内部选定的那个孩子**（见 `lib/dictationAnchor.ts`）——
   * "按谁的年级取词"只在选词预览里才有意义，那里直接让家长选**年级**，
   * 界面上**不提**"锚点"这类实现细节。
   * 管理员模式不取词，所以用不上。
   */
  studentGradeCode?: string;
}) {
  const toast = useToast();
  const qc = useQueryClient();

  const isMaintain = mode === 'maintain';
  /** 查询键里的作用域标识：管理员是 `admin`，家长是那个孩子的 id。 */
  const scopeKey = scope.kind === 'admin' ? 'admin' : scope.studentId;

  /**
   * 当前 tab。初值 = `DEFAULT_DICTATION_TAB`（**按书录入**）—— 用户原话
   * "调换标签顺序，但默认选中的标签保持不变"。
   */
  /**
   * 默认页**按角色**不同（2026-11-04 用户要求）：
   * 管理后台点「听写字表」直接进「浏览与编辑」；家长端仍是「按书看字表」。
   * 判据只有 `defaultDictationTab()` 一处。
   */
  const [tab, setTab] = useState<Tab>(defaultDictationTab(isMaintain));

  /**
   * ---- 上次的选择（本机 `localStorage`，**不上传、不入库**）----
   *
   * 用户提过"每次重选很烦"（2026-10-18），所以筛选状态记在**这台设备**上：
   * 刷新页面 / 切到别的 tab 再回来，还是上次那一套。
   *
   * ⚠️ 恢复是**逐项校验**的（`parseStoredBrowseFilter`）：坏值回落成「全部」，
   *    并把"哪几项失效了"留一句话给界面（`filterNotice`）—— 不报错、不白屏。
   * ⚠️ 恢复出来的课本 / 课文可能**已经被删了**，那要等列表到手才能判，
   *    见下面那个 `reconcileBrowseFilter` 的 effect。
   * ⚠️ 只有**选择**存下来，列表数据一条都不存（数据仍然每次从后端读）。
   * ⚠️ 存的是**点过「查询」的那一套**（`filter`），待生效的 `draft` 不进存储 ——
   *    见下面那个写回 effect。
   */
  const restored = useMemo(() => {
    const result = readBrowseFilter(scopeKey);
    /**
     * ⚠️ 课文界面隐藏时（`DICTATION_LESSONS_UI_ENABLED=false`）**不恢复课文**：
     *    那个下拉根本没渲染，留着它会让"有一项选过了"却找不到是哪一项，
     *    还会把列表收窄成空。这是"功能暂时隐藏"，不是"值失效"，所以**不提示**。
     */
    if (lessonsUiEnabled() || result.filter.lessonId === '') return result;
    return { filter: { ...result.filter, lessonId: '' }, dropped: result.dropped };
  }, [scopeKey]);

  const [filter, setFilter] = useState<FilterState>(restored.filter);
  /**
   * ---- 待生效的那一套（2026-10-18「先选、再查询」）----
   *
   * 用户原话："选好所有条件后，点查询按钮才出结果，不要选一个条件就刷新一个"。
   *
   * ⚠️ 下拉 / 勾选框 / 快捷按钮 / 重置 **只**动这一份：改它**不发任何请求**，
   *    下面的列表保持上一次的结果不动（`draft` 不参与任何 queryKey 或 `enabled`）。
   * ⚠️ 只有点「查询」（或按回车）才会 `setFilter(draft)` —— 那是**唯一**的升格入口
   *    （裁决走 `browseQueryDecision()`：没有可查条件时不许升格）。
   * ⚠️ 初值与 `filter` **相同**（恢复出来的那套）：刷新回来时下拉里显示的就是
   *    "上次查过的那一套"，不会再冒出一个"有改动"的提示。
   */
  const [draft, setDraft] = useState<FilterState>(restored.filter);
  /** 恢复时"某项已失效、已回落"的提示（中性色；没有失效项时 `null`）。 */
  const [filterNotice, setFilterNotice] = useState<string | null>(
    describeDroppedFilterValues(restored.dropped) || null,
  );

  /**
   * 每次**已生效**那套变了就写回本机（写不进去也就算了：隐私模式 / 被策略禁用）。
   *
   * ⚠️ 写的是 `filter`（点过「查询」的那套），**不是** `draft`：
   *    半截状态存下来，下次进来会按一套"从来没查过"的条件发请求，行为不可预测。
   * ⚠️ 于是刷新 / 切回来时 `filter` 的初值就是它 ⇒ 列表 `enabled` 直接为 true，
   *    **不用再点一次「查询」**（否则回来是空列表，比记不住更烦）。
   */
  useEffect(() => {
    writeBrowseFilter(scopeKey, filter);
  }, [scopeKey, filter]);

  const [importState, setImportState] = useState<ImportState>(() => initialImportState());

  // ---- 按「书 + 表」看 / 录（第五步的主视角） ----
  const [booksSelection, setBooksSelection] = useState<BooksSelection>(EMPTY_BOOKS_SELECTION);
  /** 粘贴框里的内容（一张表一份；切表时清空，见 `changeBooksSelection`）。 */
  const [listText, setListText] = useState('');
  /**
   * **本次会话里用户确认过的位置集合**（迁移 0018；2026-10-20 第七轮改口径）：
   * 键 = `${line}:${index}`，值 = 确认下来的读音。
   *
   * 只有两条路会往里加东西（都在 `pickListReading`）：
   *   · 点浮窗里一个读音（= 换音 + 确认）；
   *   · 点浮窗里的「✅ 确认就是「X」」（= 读音不变 + 确认）。
   * ⛔ 预览报告里"文件自带的读音"**绝不能**回填进来 —— 否则确认一个等于确认一屏
   *    （判据见 `lib/importConfirmedReadings.ts`）。
   *
   * ⚠️ 它属于"这一份输入"：换书/换表/改文本都要清掉（否则会把上一份文本的
   *    位置读音带到另一份里，行号还可能对得上 —— 那是最难发现的错）。
   */
  const [listReadings, setListReadings] = useState<Record<string, string>>({});
  const [listPreview, setListPreview] = useState<ImportReportView | null>(null);
  const [listCommit, setListCommit] = useState<ImportReportView | null>(null);
  const [listPreviewError, setListPreviewError] = useState<string | null>(null);
  /**
   * 预览**失败**时后端 `error.details` 里的逐条原因（行号 + 词元 + 原文 + 原因）。
   *
   * ⚠️ 用户实测（2026-10-16）：失败时界面只有「预览失败：提交的内容有误」，
   *    看不出哪一行、哪个词元有问题。`listPreviewError` 现在已经是具体那句，
   *    这里是**全部**细节（由同一个列表组件渲染）。
   */
  const [listPreviewIssues, setListPreviewIssues] = useState<ImportIssueRow[]>([]);
  const [listCommitError, setListCommitError] = useState<string | null>(null);
  const [listBusy, setListBusy] = useState<'preview' | 'commit' | null>(null);
  /** 打开的那个字的"读音与造词"（多音字按音造词）。 */
  const [readingsTarget, setReadingsTarget] = useState<HanziView | null>(null);
  /**
   * **这个浮窗是从哪一个位置点开的**（`hanzi_listing.id`，迁移 0018 之后
   * "一个字卡 = 一条收录"）。`null` = 没有位置（家长端「按书看字表」/ 手工建的字）。
   *
   * ## 为什么它必须**单独**存一份（2026-10-21 用户报的 bug）
   *
   * 用户原话：
   *
   * > "我在「浏览与编辑」模块修改了一个字的拼音，点保存后，**外面的字的拼音没有
   * >  改变**，再点进去看，却是保存新的拼音的，就是外面没有更新。"
   *
   * 字卡显示 `listingReading ?? pinyin`（这个位置标定的音优先），而"改这个字的
   * 读音"写的是 `hanzi.pinyin` —— 导入时每个位置的读音会被快照进
   * `hanzi_listing.reading`，所以改默认音**在任何卡片上都看不出来**。
   * 修法：保存时把"用户点开的那个位置"一起带上，服务端把它那一行显式 `reading`
   * 一起改掉（别的显式位置保持不动）。语义见 `dictation.service.ts` 的
   * `setHanziPinyin()`。
   *
   * ⚠️ **不能**在保存时临时读 `readingsTarget.listingId`：那个对象会被下面那个
   *    "跟着重新查回来的数据更新"的 effect 换成 `readingsQuery.data.hanzi`
   *    （`GET .../readings` 的 DTO 里**没有** `listingId`）。第一次保存之后它就
   *    丢了 —— 用户再改一次又回到"外面没变"。所以位置单独存一份，只在开窗时写。
   */
  const [readingsListingId, setReadingsListingId] = useState<string | null>(null);
  /**
   * 打开浮窗的那张卡**自己的跳转键**（`hanziRowKey(item, rowIndex)`）。
   *
   * ⚠️ 不能从 `readingsTarget.listingId` 临时算：`readingsTarget` 会被
   *    「读音汇总」DTO 换掉，而那份 DTO **没有 `listingId`** —— 换过之后
   *    当前行就不再被排除，「还出现在」里会冒出**自己**；没有 `listingId` 的兜底行
   *    还会多行共用 `id`，登记表互相覆盖后点哪行都落到第一行（2026-10-21 的 bug）。
   *    所以开窗那一刻把键原样记下来，跳转列表按它排除当前行。
   */
  const [readingsCardKey, setReadingsCardKey] = useState<string | null>(null);
  /**
   * 这个浮窗是从哪儿打开的：`browse`（浏览与编辑）/ `books`（按书看字表，**家长端**）。
   *
   * ⚠️ 用户要求"**只在同一屏内跳转**（当前筛选结果里的，不要跨筛选 / 跨册）"：
   *    只有从「浏览与编辑」打开时才列"还出现在"（那张网格才有跳转键）；
   *    「按书看字表」那张家长网格没有登记跳转，所以绝不把浏览页的结果塞进它的浮窗。
   */
  const [readingsOrigin, setReadingsOrigin] = useState<'browse' | 'books'>('browse');
  /**
   * 同一个字的不同位置之间**互相跳转**（与导入预览同一份实现，见 `hanziJump.ts`）。
   *
   * 浮窗里「这个字还出现在」的每一项点下去：先关浮窗（`body` 解锁）→ 滚到那张卡 +
   * 高亮 2 秒 + 焦点落到卡上 —— **到此为止**，不打开目标位置的浮窗
   * （2026-10-20 用户要求：跳完只高亮，别自动弹窗）。
   */
  const jump = useHanziJump();
  /**
   * 浮窗里"正在改这个字的拼音"（`null` = 没在改）。
   *
   * ⚠️ 改拼音原来挂在**字卡**上（点拼音就地编辑）。2026-10-18 本轮按用户要求
   *    "所有的操作都在这个框框里面进行"，它搬进了浮窗 —— 所以这个开关跟着浮窗走，
   *    与列表里那份 `pinyinEdit`（现在只管**词**）分开。
   */
  const [readingsPinyinOpen, setReadingsPinyinOpen] = useState(false);
  /**
   * "我正在给哪个读音造词"。
   *
   * 浮窗里每个读音下面都有「给这个音造词」：点它先记下 `{ 字, 读音 }`，再打开
   * **既有的**「新增词语」表单；保存成功之后用 `PUT .../words/:id/readings`
   * 把这个字在这个词里的读音钉到那个音上（多音字按音造词）。
   */
  const [pendingReading, setPendingReading] = useState<{ charText: string; reading: string } | null>(
    null,
  );

  /**
   * **一次造多个词**（2026-10-30）：要造的是哪个字、哪个读音。
   *
   * 存整个 `HanziView`（不只是字面）：批量造词要用它的 `textbookId / gradeCode /
   * semester`（创建词语的必填口径）。浮窗关掉之后这份数据仍然在手。
   */
  /** 选词造词（2026-11-04）：同样存整个 HanziView，词库查询要它的 id。 */
  const [pickWordTarget, setPickWordTarget] = useState<{
    hanzi: HanziView;
    reading: string;
  } | null>(null);

  const [batchWordTarget, setBatchWordTarget] = useState<{
    hanzi: HanziView;
    reading: string;
  } | null>(null);

  /**
   * ---- 「添加出版社」浮窗（2026-10-16 新增；2026-10-18 本轮改成"只填出版社名"）----
   *
   * 唯一入口：出版社下拉的**最后一项**「＋ 添加出版社」。
   * 浮窗里只有出版社名；年级 / 学期用页面那两格（预设值、始终可选）。
   * ⛔ 没有"建一册"的第二个入口（用户定稿：三格选好就能录，保存时自动建书）。
   */
  const [newTextbookOpen, setNewTextbookOpen] = useState(false);
  const [newTextbookError, setNewTextbookError] = useState<string | null>(null);

  // ---- 弹窗状态（**只在维护模式用**） ----
  const [lessonForm, setLessonForm] = useState<{ open: boolean; editing: LessonView | null }>({
    open: false,
    editing: null,
  });
  const [lessonFormError, setLessonFormError] = useState<string | null>(null);
  const [itemForm, setItemForm] = useState<{
    open: boolean;
    kind: ItemKind;
    editing: HanziView | WordView | null;
  }>({ open: false, kind: 'hanzi', editing: null });
  const [itemFormError, setItemFormError] = useState<string | null>(null);
  /** 编辑时"这一条现在关联了哪些课文"（`null` = 还没查到 / 查不到）。 */
  const [itemLessonLinks, setItemLessonLinks] = useState<string[] | null>(null);
  /** 正在查现有关联（查完之前不许提交关联字段，否则会把关联清空）。 */
  const [itemLinksLoading, setItemLinksLoading] = useState(false);
  const [itemLinksUnknown, setItemLinksUnknown] = useState(false);
  const [pinyinEdit, setPinyinEdit] = useState<{ kind: ItemKind; id: string } | null>(null);
  const [pinyinError, setPinyinError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // ---- 列表查询 ----
  const textbooksQuery = useQuery({
    queryKey: ['dictation', scopeKey, 'textbooks'] as const,
    queryFn: () => dictationApi.listTextbooks(scope),
  });

  /**
   * 课本列表（`null` = **还没拿到 / 没拿到**）。
   *
   * ⚠️ 2026-10-18：筛选里不再有 `textbookId`，"现在看的是哪一册"要靠
   *    出版社 + 年级 + 学期去解析（`findFilterVolume`）。解析不出来时：
   *      · 列表照常按「出版社 + 年级 + 学期」查（后端支持，空就空 —— 不报错）；
   *      · 「新增生字 / 新增词语」**禁用**并说明原因（它必须落到具体的一册）。
   */
  const filterTextbooks = textbooksQuery.data?.textbooks ?? null;
  const activeVolume = findFilterVolume(filterTextbooks, filter);

  /**
   * 「**已生效**那套到底选过了没有」—— 与面板用的是**同一个纯函数**
   * （`lib/dictationFilters.ts` 的 `isFilterActive`），所以"界面说不列"与
   * "没发请求"不可能走岔。
   *
   * ⚠️ 只看 `filter`（已生效），**不看** `draft`：待生效的改动不下发。
   */
  const filterActive = isFilterActive(filter);
  /**
   * 「生字 / 词语」两个列表查询的开关（`enabled` 的唯一判据）。
   *
   * 用户原话（2026-10-18）："这里没有选择任何选项，下面就不应该出现字或词的列表。"
   * 用户原话（本轮）："选好所有条件后，点查询按钮才出结果，不要选一个条件就刷新一个"。
   * ⚠️ **未选就一个请求都不发** —— 不是"先拉 200 条再藏起来"：那样既浪费，
   *    慢网下还会先闪一下列表再消失。
   * ⚠️ 判据只吃 `filter`（**已生效**那套）：`draft` 改一百遍也不会让这条变 true ——
   *    请求只可能在点过「查询」之后发出。
   * ⚠️ 恢复的筛选里带了出版社时，先等课本列表到手再发：那家出版社可能已经没有书了
   *    （见下面的 `reconcileBrowseFilter` effect），没校对就发＝白拉一次 +
   *    闪一下"0 条"。
   */
  const listsEnabled = browseListsEnabled(filter, {
    textbooksSettled: textbooksQuery.isFetched,
  });

  /**
   * **按「在哪张表上」分块**（2026-10-19 用户要求）：
   *   · 识字表 / 写字表 ⇒ 只查、只渲染字块；
   *   · 词语表 ⇒ 只查、只渲染词语块（字块连请求都不发）；
   *   · 全部表 ⇒ 两块都查。
   *
   * ⚠️ 判据用的是**已生效**那套（`filter`）：下拉里刚点「词语表」但没点「查询」时，
   *    下面仍按上一次查询的结果显示，与列表的其它部分同口径。
   * ⚠️ 这不仅是"少渲染"：另一块的**请求也不发**（`enabled` 里带上它），
   *    所以选识字表时不会白拉一份词语。
   */
  const browseBlock = browseBlocks(filter);
  const hanziListEnabled = listsEnabled && browseBlock.hanzi;
  const wordsListEnabled = listsEnabled && browseBlock.words;

  /**
   * 课文列表：按**出版社 + 年级 + 学期**筛（= 这一册）。
   *
   * ⚠️ 2026-10-18：以前这里是 `textbookId`。现在筛选里没有这个字段了，
   *    而后端 `dictationListQuerySchema` 也支持 `publisher` —— 三格齐了就是同一册。
   */
  const lessonQuery = useMemo(
    () =>
      cleanQuery({
        publisher: filter.publisher,
        gradeCode: filter.gradeCode,
        semester: filter.semester,
        limit: '200',
      }),
    [filter.publisher, filter.gradeCode, filter.semester],
  );
  const lessonsQuery = useQuery({
    queryKey: ['dictation', scopeKey, 'lessons', lessonQuery] as const,
    queryFn: () => dictationApi.listLessons(scope, lessonQuery),
    /**
     * ⚠️ 课文界面隐藏时（`DICTATION_LESSONS_UI_ENABLED=false`）**不发这个请求**：
     *    那个下拉与「课本与课文」卡片都不渲染，拉回来也没有一处会用到。
     *    开关打开时照旧（`filter.lessonId` 那一支是给"恢复的课文"用的）。
     */
    enabled: lessonsUiEnabled() || filter.lessonId !== '',
  });

  const hanziQuery = useMemo(
    () =>
      cleanQuery({
        /**
         * **出版社 + 年级 + 学期**（2026-10-18：筛选里的第一格从 `textbookId`
         * 换成了 `publisher`）。三者齐了 = 唯一一册；出版社为空 = 跨出版社筛
         * （后端支持，不要求先选出版社）。
         */
        publisher: filter.publisher,
        gradeCode: filter.gradeCode,
        semester: filter.semester,
        lessonId: filter.lessonId,
        /** **按表筛**（识字表 / 写字表）—— 第五步取代了旧的 `hanziCategory`。 */
        listType: filter.listType,
        overriddenOnly: filter.overriddenOnly ? 'true' : undefined,
        polyphonicOnly: filter.polyphonicOnly ? 'true' : undefined,
        /**
         * ⚠️ 2026-10-20：前端**不再分页**（「再显示 100 个」已删），一次取回、一次渲染。
         *    这里就是"一次能显示多少条"：与后端 schema 的 `max` 共用一个常量
         *    （`DICTATION_BROWSE_LIMIT_MAX` = 20000，见 `packages/shared/.../schemas.ts`）。
         *    真超过时列表底部会明说"服务器这次只返回了 N 条（共 M 条）"。
         */
        limit: String(DICTATION_BROWSE_LIMIT_MAX),
      }),
    [filter],
  );
  const hanziListQuery = useQuery({
    queryKey: ['dictation', scopeKey, 'hanzi', hanziQuery] as const,
    queryFn: () => dictationApi.listHanzi(scope, hanziQuery),
    /**
     * ⚠️ 未选任何筛选 ⇒ **不发请求**（判据见上面的 `listsEnabled`）。
     * ⚠️ 选「词语表」时**字块不渲染**，这里也一并关掉（不白拉）。
     */
    enabled: hanziListEnabled,
  });

  const wordQuery = useMemo(
    () =>
      cleanQuery({
        /** 同字列表：**出版社 + 年级 + 学期**（不再是 `textbookId`）。 */
        publisher: filter.publisher,
        gradeCode: filter.gradeCode,
        semester: filter.semester,
        lessonId: filter.lessonId,
        wordCategory: filter.wordCategory,
        listType: filter.listType,
        fromTextbookOnly: filter.fromTextbookOnly ? 'true' : undefined,
        overriddenOnly: filter.overriddenOnly ? 'true' : undefined,
        polyphonicOnly: filter.polyphonicOnly ? 'true' : undefined,
        /** 与字块同一条口径：一次取回、一次渲染（见上面的 `DICTATION_BROWSE_LIMIT_MAX`）。 */
        limit: String(DICTATION_BROWSE_LIMIT_MAX),
      }),
    [filter],
  );
  const wordsListQuery = useQuery({
    queryKey: ['dictation', scopeKey, 'words', wordQuery] as const,
    queryFn: () => dictationApi.listWords(scope, wordQuery),
    /**
     * ⚠️ 同上：未选任何筛选 ⇒ **不发请求**；选识字表 / 写字表时词语块不渲染，
     *    这个请求也一并关掉。
     */
    enabled: wordsListEnabled,
  });

  /**
   * 点「查询」（以及桌面端在筛选区按回车）—— **唯一**让待生效那套下发的入口。
   *
   * 三种情况：
   *   1. 没有可查条件 → 什么都不做（按钮那时是禁用的）；
   *   2. 与已生效那套**逐项相同** → 语义是"再查一次"，直接 refetch 两个列表
   *      （不 setState：条件没变就没有新的 queryKey，重新发一遍才是用户点的意思）；
   *   3. 有变化 → 把待生效那套升成已生效（新 queryKey ⇒ 请求这时才发）。
   */
  const applyQuery = () => {
    const decision = browseQueryDecision(draft, filter);
    if (!decision.allowed) return;
    /** 点了「查询」，上次"某项失效"这件事就翻篇了。 */
    setFilterNotice(null);
    /** 换条件 / 重新查：上一次还没落地的"跳过去"作废（否则会滚到新列表里不相干的位置）。 */
    jump.cancel();
    if (!decision.changed) {
      /**
       * ⚠️ 只 refetch **当前渲染的那一块**：选识字表时词语块不渲染/不查询，
       *    对它 refetch 只会多发一个请求（`enabled=false` 的查询本来也不该被叫醒）。
       */
      if (browseBlock.hanzi) void hanziListQuery.refetch();
      if (browseBlock.words) void wordsListQuery.refetch();
      return;
    }
    setFilter(decision.next);
  };

  /**
   * 「重置」：**只清待生效**那套 —— 不查询、不动已生效那套（列表保持上一次的结果）。
   *
   * ⚠️ 用户原话："重置/清空只清待生效的值，不要顺手查询"。
   */
  const resetDraftFilter = () => {
    setDraft({ ...EMPTY_BROWSE_FILTER });
    setFilterNotice(null);
  };

  /**
   * 恢复的筛选里可能有**已经失效**的值：那家出版社没有书了 / 那节课没了。
   *
   * 课本列表到手后（`isSuccess`）对照一次：失效的项回落成「全部」，
   * 并把"哪一项失效了"说一句人话（中性色，**不是错误态** —— 数据变了不是用户的错）。
   *
   * ⚠️ 2026-10-18：核对的是**出版社还在不在**（课本列表去重后的那一份），
   *    不再是 `textbookId`（那个字段已经不在筛选里了）。
   * ⚠️ **两套一起清**：已生效那套回落之后，"待生效"那套里如果还留着那个已删的
   *    出版社，下拉里就会显示一个查不出东西的值（看起来"选过了"，却永远空）。
   *    清完两套又一致 ⇒ 不会再冒出"有改动"的提示。
   * ⚠️ `reconcileBrowseFilter` 在没有失效项时返回**同一个引用**，所以这里可以
   *    直接用 `result.filter === filter` 提前退出，不会来回 setState。
   * ⚠️ 拿不到课本列表（解码成 `null`）时**不判失效**：宁可留着，也不要把用户
   *    记住的选择因为一次网络失败清掉。
   */
  useEffect(() => {
    if (!textbooksQuery.isSuccess) return;
    const textbooksList = textbooksQuery.data?.textbooks ?? null;
    const lessonsList = lessonsUiEnabled() ? (lessonsQuery.data?.lessons ?? null) : null;
    const result = reconcileBrowseFilter(filter, {
      publishers: textbooksList === null ? null : publisherFilterOptions(textbooksList),
      lessonIds: lessonsList === null ? null : lessonsList.map((item) => item.id),
    });
    if (result.filter === filter) return;
    setFilter(result.filter);
    setDraft((prev) => clearBrowseFilterFields(prev, result.dropped));
    setFilterNotice(describeDroppedFilterValues(result.dropped));
  }, [filter, textbooksQuery.isSuccess, textbooksQuery.data, lessonsQuery.data]);

  // ---- 按「书 + 表」：这一册的收录数 + 这一张表的内容 ----
  /**
   * 三格（出版社 + 年级 + 学期）定位到的那一册。
   *
   * ⚠️ `null` = **书还没建过**（不是"没有选择"）：这**不影响录入** ——
   *    面板照常显示三张表（计数 0）、照常能预览 / 导入，保存时后端自动建书。
   *    年级 / 学期是预设值、始终可编辑，所以换学期就是换一册。
   */
  const activeBooksVolume = useMemo(
    () => findPublisherVolume(textbooksQuery.data?.textbooks ?? null, booksSelection),
    [
      textbooksQuery.data,
      booksSelection.publisher,
      booksSelection.gradeCode,
      booksSelection.semester,
    ],
  );
  /** 三格齐了就算"选好了这一本书"（空值项 = 还没选好，不是第一册）。 */
  const booksQueryComplete =
    booksSelection.publisher !== '' &&
    booksSelection.gradeCode !== '' &&
    booksSelection.semester !== '';

  /**
   * 当前选中出版社的**人话**（`人民教育出版社`）。
   *
   * ⚠️ 提交给后端时用它 —— 服务层按 `publisher` **或** `name` 那一列的人话匹配；
   *    下拉的 value 只是界面内部的稳定键（小写、去空白），不是库里的值。
   */
  const booksPublisherLabel = useMemo(
    () => publisherLabelOf(textbooksQuery.data?.textbooks ?? null, booksSelection.publisher),
    [textbooksQuery.data, booksSelection.publisher],
  );

  /**
   * 选中的那一册的三张表各多少 —— **从课本列表里取**（`gradeCounts`），
   * 不额外打一次接口：那一份数据本来就在。
   *
   * ⚠️ 三格齐了但**书还没建过** → 三张表按 **0** 显示（用户要求"没那册就都是 0"），
   *    并且**照常能录**；**不是**「—」（那是"数据没拿到"）。
   * ⚠️ 三格没齐 / 没有出版社 → `null`（显示「—」）。
   */
  const booksCounts = useMemo(() => {
    if (activeBooksVolume === null) {
      /**
       * ⚠️ 列表**没拿到**（`data === undefined`）时不能说 0：那是「—」。
       *    只有"列表拿到了、三格齐了、确实没有这一册"才按 0 显示。
       */
      if (!booksQueryComplete || textbooksQuery.data === undefined) return null;
      return {
        gradeCode: booksSelection.gradeCode,
        semester: booksSelection.semester,
        counts: { shizi: 0, xiezi: 0, cibiao: 0 },
      };
    }
    if (activeBooksVolume.gradeCounts === null) return null;
    return (
      activeBooksVolume.gradeCounts.find(
        (entry) =>
          entry.gradeCode === booksSelection.gradeCode &&
          entry.semester === booksSelection.semester,
      ) ?? { gradeCode: booksSelection.gradeCode, semester: booksSelection.semester, counts: {} }
    );
  }, [
    activeBooksVolume,
    booksQueryComplete,
    textbooksQuery.data,
    booksSelection.gradeCode,
    booksSelection.semester,
  ]);

  /**
   * ② 「这一张表里有什么」的数据 —— **只有家长端查，管理员端一个请求都不发**。
   *
   * ## 为什么管理员端不查（上一轮那条注释仍然成立）
   *
   * 用户说"管理员端录入字词的那一块不需要再显示字词了，因为你已经有浏览那一块"，
   * 所以维护模式的 ② 仍然不渲染；这里也就不为他发 `GET .../lists/items`。
   *
   * ## 为什么家长端**要**查（本轮修正）
   *
   * 用户紧接着澄清："**我不是说家长端那块字卡要删除**" —— 家长端 ② 恢复成
   * 只读字卡网格，数据只能从这一个端点来（`{hanzi, words, total}`）。
   *
   * ⚠️ `enabled` 的三个条件缺一不可：
   *    · `!isMaintain` —— 管理员端不渲染 ②，查回来没人用；
   *    · `tab === 'books'` —— 切到别的 tab 就别再挂着这个查询；
   *    · `activeBooksVolume !== null` —— **书还没建过时没有 textbookId 可查**
   *      （面板会说"这一册还没有录入"，而不是报错或显示 0）。
   * ⚠️ 关键词/分页都不传：② 是"这一张表里有什么"的**全量**视图（`limit` 默认 200，
   *    够一册字表；超过时面板会明说"这里显示前 N 个"）。
   */
  const booksItemsQuery = useQuery({
    queryKey: [
      'dictation',
      scopeKey,
      'lists',
      'items',
      activeBooksVolume?.id ?? 'none',
      booksSelection.gradeCode,
      booksSelection.semester,
      booksSelection.listType,
    ] as const,
    queryFn: () =>
      dictationApi.fetchListItems(scope, {
        textbookId: activeBooksVolume?.id ?? '',
        gradeCode: booksSelection.gradeCode,
        semester: booksSelection.semester,
        listType: booksSelection.listType,
      }),
    enabled:
      !isMaintain && tab === 'books' && booksQueryComplete && activeBooksVolume !== null,
  });

  // ---- 读音与造词浮层（第五步；2026-10-18 起它就是这个字的**唯一**操作入口） ----
  /**
   * 「这个字的读音汇总」的查询键 —— **只有这一处**拼它。
   *
   * ⚠️ 隐藏读音的**乐观更新**要往这个键里写（`setQueryData`），所以键必须能被
   *    别处引用。两份手写的键（一份在 `useQuery`、一份在 mutation 里）迟早漂移，
   *    那时乐观更新写进一个没人读的缓存，表现就是"点了没反应"。
   */
  const readingsKey = (hanziId: string) =>
    ['dictation', scopeKey, 'hanzi', hanziId, 'readings'] as const;

  const readingsQuery = useQuery({
    queryKey: readingsKey(readingsTarget?.id ?? 'none'),
    queryFn: () => dictationApi.fetchHanziReadings(scope, readingsTarget?.id ?? ''),
    enabled: readingsTarget !== null,
  });

  /**
   * 浮窗里的那个字要跟着**重新查回来的数据**更新。
   *
   * ⚠️ 为什么需要：浮窗里的「标为多音字」「改拼音」写完都会 `refreshAll()`，
   *    而 `readingsTarget` 是打开那一刻抓下来的**旧快照** —— 不同步的话，
   *    按钮会一直显示"标为多音字"（其实已经标过了），用户以为没生效。
   * ⚠️ 只在**这些字段真的变了**时才 `setState`：否则每次数据刷新都会换一个对象，
   *    effect 自己触发自己（无限循环）。
   */
  useEffect(() => {
    const fresh = readingsQuery.data?.hanzi;
    if (fresh === undefined || readingsTarget === null) return;
    if (fresh.id !== readingsTarget.id) return;
    if (
      fresh.pinyin === readingsTarget.pinyin &&
      fresh.pinyinOverridden === readingsTarget.pinyinOverridden &&
      fresh.polyphonic === readingsTarget.polyphonic &&
      fresh.note === readingsTarget.note &&
      fresh.gradeCode === readingsTarget.gradeCode &&
      fresh.semester === readingsTarget.semester
    ) {
      return;
    }
    /**
     * ⚠️ **把"这一行"的位置字段从旧快照搬回来**：`GET .../readings` 的 DTO
     *    没有 `listingId` / `listingReading` / `listingLineNo`。直接换成 `fresh`
     *    会让当前卡的身份退化成 `id`（同字 N 行就分不清哪一行是"自己"）——
     *    跳转列表会把当前行当成"别的位置"，然后自己跳到自己。
     */
    setReadingsTarget({
      ...fresh,
      listingId: readingsTarget.listingId ?? null,
      listingReading: readingsTarget.listingReading ?? null,
      listingSortOrder: readingsTarget.listingSortOrder ?? null,
      listingLineNo: readingsTarget.listingLineNo ?? null,
    });
  }, [readingsQuery.data, readingsTarget]);

  /**
   * 「隐藏不常用的音」—— **服务端是唯一来源**（2026-10-18 用户定稿）。
   *
   * 用户原话："**第一个，隐藏读音要存服务器。**" 于是一整条 localStorage 通道
   * （旧 `lib/useHiddenReadings.ts`）**已删除**，这里只做一件事：
   * 把**接口给的那一份**算成"这个字现在藏了哪几个音"。
   *
   * 取值顺序（都是服务端数据，没有本机记忆）：
   *   1. `GET .../readings` 的顶层 `hiddenReadings`（浮窗唯一数据源；`null` = 没拿到）；
   *   2. 回落到列表 DTO 里的 `hanzi.hiddenReadings`（打开那一刻的快照，`[]` = 没藏）。
   *
   * ⚠️ 只保留**这个字现在确实有的读音**（`splitReadingsByHidden` 同口径）：
   *    库里的值是历史存量时可能包含一个已经不属于这个字的音（改过拼音覆盖 /
   *    拼音库变了），把它原样发回去会被后端 400 拒掉（那是**对的**，
   *    见 `normalizeHiddenReadings`）—— 所以这里先切掉，用户点一下不会莫名失败。
   *
   * ⚠️ 隐藏只影响**显示**：浮窗把藏起来的收进「已隐藏的读音（N）」；
   *    已有的词、它们的读音（`word_hanzi.reading`）、听写取词与播放一个字都不动。
   */
  const hiddenReadings = useMemo(() => {
    const server = readingsQuery.data?.hiddenReadings ?? readingsTarget?.hiddenReadings ?? [];
    const all = readingsQuery.data?.summary.readings ?? readingsTarget?.readings ?? [];
    return splitReadingsByHidden(all, server).hidden;
  }, [readingsQuery.data, readingsTarget]);

  /**
   * **旧版 localStorage 的键，清一次**（用户要求"旧值不要报错"）。
   *
   * 旧实现把隐藏读音记在 `fht.dictation.hiddenReadings.<id>`；现在服务端是唯一来源，
   * 于是那些键既不会被读、也不该继续占着地方（留着会让下一个人以为它还是真相）。
   * 清掉了就在底部提示一句 —— **一句话就够**，不弹窗、不拦任何操作。
   *
   * ⚠️ 只在挂载时跑一次（清完就没有了）；清不掉（隐私模式）静默跳过。
   */
  useEffect(() => {
    const removed = purgeLegacyHiddenReadings();
    if (removed > 0) {
      toast.showToast(
        `隐藏读音已改为云端保存：本机旧的 ${removed} 条记录已清除，现在的显示跟着服务器走`,
        'info',
      );
    }
    /** 只在挂载时跑一次（`toast` 是稳定的 context 值）。 */
  }, []);

  // ---- 选词预览（**只有家长模式有**：管理员的读路径上没有取词接口） ----
  const canPick = scope.kind === 'parent' && !isMaintain;
  const pickStudentId = scope.kind === 'parent' ? scope.studentId : '';
  const [pickResult, setPickResult] = useState<{
    applied: dictationApi.PickAppliedView | null;
    words: dictationApi.PickedWordView[] | null;
    total: number | null;
    wrongTotal: number | null;
    note: string | null;
    missing: boolean;
  } | null>(null);
  /** 取词失败时的红条（只装"真失败"，不留一个永远不会出现的状态）。 */
  const [pickNotice, setPickNotice] = useState<string | null>(null);
  const pickMutation = useMutation({
    mutationFn: (payload: dictationApi.PickPayload) => dictationApi.pickWords(scope, payload),
    onSuccess: (data) => {
      setPickNotice(null);
      setPickResult({
        applied: data.applied,
        words: data.words,
        total: data.total,
        wrongTotal: data.wrongTotal,
        note: data.note,
        missing: data.words === null,
      });
    },
    onError: (err: unknown) => {
      const failure = dictationApi.describeApiFailure(err);
      setPickNotice(`取词失败：${failure.message}`);
    },
  });

  /**
   * 错词本的数量 —— 只给"取词来源"那个下拉当提示（"错词本里还有 7 个没掌握"）。
   *
   * ⚠️ 它与选词结果**没有从属关系**（不是"这次取到的错词数"，那是 `wrongTotal`），
   *   所以单独一个查询。失败/拿不到时 `data` 是 `undefined` → 界面说"数量没拿到"，
   *   而**不是**说"错词本是空的"（本项目反复踩过这条线）。
   */
  const mistakeSummaryQuery = useQuery({
    queryKey: ['dictation-mistakes', pickStudentId, 'summary'] as const,
    queryFn: () => sessionApi.listMistakes(pickStudentId, { limit: 1 }),
    enabled: canPick && pickStudentId !== '',
  });
  const mistakeSummary = mistakeSummaryQuery.data ?? null;

  /**
   * 「只听某个读音」的候选 = **技术层有多个读音**的字（与教学口径的"多音字"无关）。
   *
   * ⚠️ 2026-10-18：「只看多音字」筛选改成**管理员人工标注**之后，这里**不能**
   *    再用 `polyphonicOnly: 'true'` 了 —— 那会变成"只取被标注过的 4 个字"，
   *    而这一格要的是"拼音库里有 2+ 读音"的字（`readings.length > 1`），
   *    两者是两个概念（见 `dictation.service.ts` 的 `HanziDto.polyphonic` 注释）。
   *    所以这里取一页 200 条，再在客户端按 `readings.length > 1` 过滤后取 6 个候选。
   * ⚠️ `enabled` 只按"是不是家长模式"：进这一页时顺手取回，切到「选词预览」
   *    那一刻就有数据，不会先闪一句"没拿到"。
   */
  const readingOptionsQuery = useQuery({
    queryKey: ['dictation', scopeKey, 'hanzi', 'reading-options'] as const,
    queryFn: () => dictationApi.listHanzi(scope, { limit: '200' }),
    enabled: canPick,
  });
  /**
   * 三态：`null` = **没拿到**（接口失败 / 字段名不符），`[]` = 真没有多音字。
   * 两者在界面上是**不同的两句话**（见 `DictationPickPanel`）。
   */
  const readingOptions = useMemo(() => {
    const list = readingOptionsQuery.data?.hanzi;
    if (list === undefined || list === null) return null;
    return list
      .filter((item) => item.readings.length > 1)
      .slice(0, 6)
      .map((item) => ({ char: item.charText, readings: item.readings }));
  }, [readingOptionsQuery.data]);

  // ---- 失效与刷新 ----
  const refreshAll = async () => {
    await qc.invalidateQueries({ queryKey: ['dictation', scopeKey] });
  };

  /**
   * **隐藏 / 显示一个读音**（2026-10-18：写服务端，只有管理员有这个入口）。
   *
   * ## 为什么是「乐观更新 + 失败回滚」
   *
   * 用户点「隐藏这个音」的期望是**立刻**收起来。等一次往返（手机上几百毫秒）
   * 再变，看起来像没点中，于是会再点一次 —— 那第二次点的就是"显示回来"。
   * 所以：
   *   · `onMutate` 先把新集合写进**读音查询的缓存**（界面立刻变）；
   *   · 失败时用 `onError` 把那一份**原样放回去**，再弹一条红 toast 说清原因；
   *   · 成功时用**服务端返回的那一份**覆盖乐观值（服务端是唯一真相：它可能
   *     去重 / 丢空串），并 `refreshAll()` 让字卡列表跟上。
   *
   * ⚠️ 只改 `hidden_readings` 这一列（后端保证）：已有的词、读音、听写都不动。
   * ⚠️ 家长端**永远不调**它（浮窗里连按钮都不渲染，见下面的 `isMaintain` 判断）。
   */
  const hiddenReadingsMutation = useMutation({
    mutationFn: (vars: { id: string; hidden: string[] }) =>
      adminApi.setHanziHiddenReadings(vars.id, vars.hidden),
    onMutate: async (vars) => {
      const key = readingsKey(vars.id);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<dictationApi.HanziReadingsView | null>(key);
      if (previous !== null && previous !== undefined) {
        /**
         * ⚠️ 先拼成一个**具名对象**再交给 `setQueryData`：直接传字面量时
         *    react-query 的 `Updater` 重载会把字面量往"更新函数"那一支上推，
         *    报出一个看不懂的 `string[] is not assignable to undefined`。
         */
        const optimistic: dictationApi.HanziReadingsView = {
          ...previous,
          hiddenReadings: vars.hidden,
          hanzi: { ...previous.hanzi, hiddenReadings: vars.hidden },
        };
        qc.setQueryData(key, optimistic);
      }
      return { key, previous };
    },
    onError: (err: unknown, _vars, context) => {
      /** 回滚：把点之前那一份放回去（`undefined` = 那时缓存里本来就没有东西）。 */
      if (context?.previous !== undefined) qc.setQueryData(context.key, context.previous);
      toast.showToast(`隐藏读音没保存：${toErrorMessage(err)}`, 'error');
    },
    onSuccess: async (data, vars, context) => {
      /** 以**服务端返回的那一份**为准（它做过去重 / 空串处理）。 */
      const key = context?.key ?? readingsKey(vars.id);
      const previous = context?.previous;
      if (previous !== null && previous !== undefined) {
        const confirmed: dictationApi.HanziReadingsView = {
          ...previous,
          hiddenReadings: data.hiddenReadings,
          hanzi: data,
        };
        qc.setQueryData(key, confirmed);
      }
      await refreshAll();
    },
  });

  /**
   * 浮窗里点「隐藏这个音 / 显示」—— 算出**下一份整组**并交给上面的 mutation。
   *
   * ⚠️ 只给维护模式接（家长端传 `undefined` ⇒ 浮窗里那两个按钮不渲染）。
   */
  const toggleHiddenReadingOnServer = (reading: string) => {
    if (readingsTarget === null) return;
    const next = toggleHiddenReading(hiddenReadings, reading);
    hiddenReadingsMutation.mutate({ id: readingsTarget.id, hidden: next });
  };

  // ---- 整块导入（状态机在 lib/dictationImport.ts，这里只负责"发请求 + 迁移状态"） ----
  const [importBusy, setImportBusy] = useState(false);

  const runPreview = async () => {
    /**
     * ⚠️ 这份 payload 就是状态机会记下来的那一份（`previewSucceeded(prev, payload, report)`），
     * 提交时用的也是它 —— 这就是"预览与提交是同一份输入"的落点。
     * 构造走 `lib/dictationWrite.ts` 的 `buildImportRequest`（纯函数、有单测）。
     */
    const payload = buildImportRequest(importState.input);
    /**
     * ⚠️ 本地校验走**同一份纯函数**（`validateImportInput`）—— 而不是在这里
     *    再手写一遍 `text` / 长度判断。它同时也是组件里那行黄色提示的判据，
     *    两处用同一个函数才不会出现"提示说没问题、点了却报错"。
     *
     * ⚠️ 它包含**课本按册建**的校验（选了课本就必须有年级 / 学期）：
     *    缺册别时连预览都不发 —— 但按钮**不**禁用（点了会看到明确原因，
     *    而不是灰着不说话）。
     */
    const problem = validateImportInput(importState.input);
    if (problem !== null) {
      setImportState((prev) => previewFailed(prev, problem));
      return;
    }
    setImportState((prev) => startPreview(prev));
    setImportBusy(true);
    try {
      const report = await adminApi.previewImport(payload);
      setImportState((prev) => previewSucceeded(prev, payload, report));
    } catch (err) {
      setImportState((prev) =>
        previewFailed(prev, describeImportFailure(err), describeImportFailureDetails(err)),
      );
    } finally {
      setImportBusy(false);
    }
  };

  const runCommit = async () => {
    const payload = importState.previewPayload;
    if (payload === null) return;
    setImportState((prev) => startCommit(prev));
    setImportBusy(true);
    try {
      const report = await adminApi.commitImport(payload);
      setImportState((prev) => commitSucceeded(prev, report));
      toast.showToast(
        `导入完成：新增 ${report.summary.created ?? '—'} 条、更新 ${report.summary.updated ?? '—'} 条`,
        'success',
      );
      await refreshAll();
    } catch (err) {
      setImportState((prev) =>
        commitFailed(prev, describeImportFailure(err), describeImportFailureDetails(err)),
      );
    } finally {
      setImportBusy(false);
    }
  };

  // ---------------------------------------------------------------- 按「书 + 表」

  /**
   * 换出版社 / 换年级 / 换学期 / 换表：**清掉粘贴框与预览结果**。
   *
   * ⚠️ 不能"换个表继续用旧的预览" —— 预览结果说的是"在上一张表上会新增哪些"，
   *    拿它去提交到另一张表，就是把字录到了错的表上（而界面上看不出来）。
   * ⚠️ 三格**各自独立**（年级 / 学期是预设值、始终可编辑）：换学期就是换一册，
   *    **不再**从书上反推、也不清空另外两格。有没有那一册由 `activeBooksVolume` 现算；
   *    没有也**不影响录** —— 保存时后端自动建（见 `resolveBooksImportTarget`）。
   */
  const changeBooksSelection = (patch: Partial<BooksSelection>) => {
    setBooksSelection((prev) => ({ ...prev, ...patch }));
    setListText('');
    setListReadings({});
    setListPreview(null);
    setListCommit(null);
    setListPreviewError(null);
    setListPreviewIssues([]);
    setListCommitError(null);
  };

  /**
   * 打开「添加出版社」浮窗（唯一入口：出版社下拉的最后一项）。
   *
   * ⚠️ 浮窗里只有出版社名；年级 / 学期取页面那两格。
   * ⛔ 不再有"预填当前出版社"的第二条路（用户定稿：没有"先建这一册"这个动作）。
   */
  const openAddPublisher = () => {
    setNewTextbookError(null);
    setNewTextbookOpen(true);
  };

  /**
   * 这一批字表发去哪本书（**纯函数**，有单测）：
   * 已有那一册 → `textbookId`；书还没建过 → `publisher + 年级 + 学期`。
   * 三格没齐 → `null`（按钮那会儿也不该发请求）。
   */
  const listImportTarget = resolveBooksImportTarget({
    activeTextbookId: activeBooksVolume?.id ?? null,
    publisherLabel: booksPublisherLabel,
    gradeCode: booksSelection.gradeCode,
    semester: booksSelection.semester,
  });

  /**
   * 把「本次会话确认过的位置」变成请求体里的 `readings`（空 = 不发这个键）。
   *
   * ⚠️ 这里的 `listReadings` **只**放用户真的选过 / 确认过的位置
   *    （见 `lib/importConfirmedReadings.ts`）；预览返回时绝不把文件自带的
   *    读音塞进来，否则确认一个 = 确认一整屏。
   */
  const readingsPayload = (overrides: Record<string, string>) =>
    confirmedReadingsPayload(overrides);

  const runListPreview = async (overrides: Record<string, string> = listReadings) => {
    if (listImportTarget === null) return;
    setListPreviewError(null);
    setListPreviewIssues([]);
    setListCommit(null);
    setListBusy('preview');
    try {
      const report = await adminApi.importList(
        buildListImportRequest({
          ...listImportTarget,
          listType: booksSelection.listType,
          text: listText,
          readings: readingsPayload(overrides),
          dryRun: true,
        }),
      );
      setListPreview(report);
      /**
       * ⚠️ 后端可能会把"选了的读音"归一（例如 `shuang1` → `shuāng`）—— 以报告为准
       *    同步回来，否则界面上的选中态与提交用的不是同一份。
       *
       * ⚠️ 2026-10-20（第七轮）**只同步用户真的选过/确认过的位置**（`prev` 里的键）：
       *    改造前这里把报告里**所有**有读音的位置都塞进 `listReadings`（包括文件里
       *    自带的、连 `?` 都没确认的那些），下一次预览就会把它们当"用户确认过"
       *    一起回传 —— 于是**点一个确认按钮，满屏的黄卡同时变绿**。
       *    语义与判据在 `lib/importConfirmedReadings.ts`（纯函数、有单测）。
       */
      setListReadings((prev) => syncConfirmedReadings(prev, report.readings?.positions ?? []));
    } catch (err) {
      /**
       * ⚠️ 失败时也**清掉旧预览**：否则"确认导入"会拿着上一份 `ok=true` 的报告
       *    继续可点（提交的是旧的一半输入，用户以为已经标好了）。
       */
      setListPreview(null);
      setListPreviewError(describeImportFailure(err));
      setListPreviewIssues(describeImportFailureDetails(err));
    } finally {
      setListBusy(null);
    }
  };

  /**
   * **点字卡 → 选这个位置的读音 / 确认这个音**（迁移 0018；2026-10-20 第七轮）。
   *
   * 两条路都进这里（用户明确"两种都能走"）：
   *   · 点一个读音 = 换成它 + 确认；
   *   · 点「✅ 确认就是「dé」」= 读音不变 + 确认。
   *
   * 选完立刻带上一份"这个位置 + 之前所有已确认的音"重新预览 —— 于是
   * "有没有确认完 / 有没有同字同音"这两句提示立刻更新，提交按钮也跟着启用。
   * ⚠️ `listReadings` 是**本次会话确认过的位置集合**（改文本 / 换表时清空）。
   */
  const pickListReading = (line: number, index: number, reading: string) => {
    const next = rememberConfirmedReading(listReadings, line, index, reading);
    setListReadings(next);
    void runListPreview(next);
  };

  const runListCommit = async () => {
    if (listImportTarget === null || listPreview === null) return;
    setListCommitError(null);
    setListBusy('commit');
    try {
      const report = await adminApi.importList(
        buildListImportRequest({
          ...listImportTarget,
          listType: booksSelection.listType,
          text: listText,
          readings: readingsPayload(listReadings),
          dryRun: false,
        }),
      );
      setListCommit(report);
      toast.showToast(
        /**
         * ⚠️ 书是这次保存时**自动建出来**的话要说一句（用户否则不知道它去哪了）：
         *    后端在响应的 `list.textbookCreated` 里给了这个事实。
         */
        report.list?.textbookCreated === true
          ? `已录入：新增 ${report.summary.created ?? '—'} 条、更新 ${report.summary.updated ?? '—'} 条；` +
              `这本书也顺手建好了（${booksPublisherLabel}）`
          : `已录入：新增 ${report.summary.created ?? '—'} 条、更新 ${report.summary.updated ?? '—'} 条`,
        'success',
      );
      setListText('');
      setListReadings({});
      setListPreview(null);
      /**
       * ⚠️ 刷新后三格会解析到**刚建出来的那一册**（如果有）⇒ `listItemsQuery` 的
       *    queryKey 自己就变了、自动重新取；`refreshAll` 也已经把它一起失效。
       *    不要在这里 `listItemsQuery.refetch()`：那会拿**旧的空 id** 再打一次
       *    `/textbooks//lists`（书还没有时的 key），是白发的 404。
       */
      await refreshAll();
    } catch (err) {
      setListCommitError(describeImportFailure(err));
    } finally {
      setListBusy(null);
    }
  };

  // ---- 添加出版社 / 建一册（**维护模式**） ----
  //
  // 端点用的是**既有**的 `POST /admin/dictation/textbooks`（本轮不加端点、不加迁移）。
  // 浮窗里只有出版社名；年级 / 学期来自页面那两格（没选中课本时可选）。
  // 三条路都通向"这一册已经有了"，而对用户来说是同一件事：**那一册被选中了**。
  //   1. 提交前先在**已加载的列表**里找（同出版社 + 同年级 + 同学期）→ 不发请求；
  //   2. 后端唯一键 `(publisher, subject, grade_code, semester)` 撞上 → 409 `CONFLICT`；
  //   3. 409 之后**重新拉一次列表**再找一遍（并发下"刚刚别人建了"也能接住）。
  const newTextbookMutation = useMutation({
    mutationFn: async (input: NewTextbookInput): Promise<NewTextbookOutcome> => {
      const loaded = textbooksQuery.data?.textbooks ?? null;
      const existing = findExistingTextbook(loaded, input);
      if (existing !== null) return { created: false, textbook: existing, input };

      try {
        const created = await adminApi.createTextbook(buildCreateTextbookPayload(input));
        return { created: true, textbook: created, input };
      } catch (err) {
        /**
         * ⚠️ 只有**唯一键冲突**走"选中已有那本"这条路（后端回 409 `CONFLICT`）。
         *    别的失败（断网、500、字段被拒）原样抛给 `onError` —— 把那些也
         *    说成"已经有过了"会把真的错误藏起来。
         */
        if (!(err instanceof ApiError) || err.code !== ERROR_CODES.CONFLICT) throw err;

        let fresh: TextbookView[] | null = null;
        try {
          fresh = (await dictationApi.listTextbooks(scope)).textbooks;
        } catch {
          fresh = null; // 拉不到就仍然按"撞了但找不到"处理（下面那句人话）
        }
        const found = findExistingTextbook(fresh, input);
        if (found === null) throw err;
        return { created: false, textbook: found, input };
      }
    },
    onSuccess: async (outcome) => {
      /** 先刷新列表，再切选中项：否则下拉里暂时还没有这一本（会闪一下"没有册"）。 */
      await refreshAll();
      /**
       * ⚠️ 选中新建的那本，并把**它自己的年级 / 学期**一起填上：一本书 = 一册，
       *    选中它就是要录这一册的字表（界面那两格只读）。
       */
      /**
       * ⚠️ 选中新建的那一册，并把**它自己的年级 / 学期**一起填上：一本书 = 一册，
       *    选中它就是要录这一册的字表（界面那两格变回只读）。
       * ⚠️ 重复时不报错、走同一条路：`outcome.textbook` 是**已有那一册**。
       */
      changeBooksSelection(selectionOfVolume(outcome.textbook));
      setNewTextbookOpen(false);
      setNewTextbookError(null);
      /**
       * ⚠️ 提示里必须带「出版社 · 年级 · 学期」（成功）或**已有那一册**的样子（重复）：
       *    同一家出版社下会有多册，只说"已添加出版社"没法让人确认建对了。
       */
      toast.showToast(
        outcome.created
          ? describeNewTextbookCreated(outcome.input)
          : describeNewTextbookAlreadyExists(describeTextbookLabel(outcome.textbook)),
        outcome.created ? 'success' : 'info',
      );
    },
    onError: (err: unknown) => setNewTextbookError(toErrorMessage(err)),
  });

  // ---- 课文增改删（维护模式） ----
  const lessonMutation = useMutation({
    mutationFn: async (vars: { editing: LessonView | null; payload: LessonFormSubmit }) => {
      const { editing, payload } = vars;
      /**
       * ⚠️ 载荷构造走 `lib/dictationWrite.ts` 的**纯函数**（有单测）：
       *   可选项"没填就不发这个键"（`gradeCode: ''` 会被后端拒；
       *   `lessonNo` 留空要留给后端从课序写法里取数字）。
       */
      if (editing === null) {
        return adminApi.createLesson(buildCreateLessonPayload(payload));
      }
      return adminApi.updateLesson(editing.id, buildUpdateLessonPayload(payload));
    },
    onSuccess: async () => {
      toast.showToast('课文已保存', 'success');
      setLessonForm({ open: false, editing: null });
      setLessonFormError(null);
      await refreshAll();
    },
    onError: (err: unknown) => setLessonFormError(toErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: async (target: DeleteTarget) => {
      if (target.kind === 'hanzi') return adminApi.deleteHanzi(target.id);
      if (target.kind === 'word') return adminApi.deleteWord(target.id);
      return adminApi.deleteLesson(target.id);
    },
    onSuccess: async (deleted) => {
      if (deleted) toast.showToast('已删除', 'success');
      else toast.showToast('这一条本来就已经不在了（可能刚被删过）', 'info');
      setDeleteTarget(null);
      setDeleteError(null);
      await refreshAll();
    },
    onError: (err: unknown) => setDeleteError(toErrorMessage(err)),
  });

  // ---- 字 / 词 新增与编辑（维护模式） ----
  const itemMutation = useMutation({
    mutationFn: async (vars: {
      kind: ItemKind;
      editing: HanziView | WordView | null;
      payload: ItemFormSubmit;
    }) => {
      const { kind, editing, payload } = vars;
      /**
       * **新增要写进哪一册**（2026-10-18）。
       *
       * ⚠️ 以前是 `filter.textbookId`（"已生效的那本书"）。筛选里现在没有这个字段了，
       *    落点改为「出版社 + 年级 + 学期」解析出的那一册（`activeVolume`）。
       * ⚠️ 解析不到 ⇒ 界面上那两个按钮**是禁用的**（`describeCreateTargetGap` 说明原因），
       *    正常路径走不到这里；真走到了也绝不拿别的书兜底 —— 那会把字写进错的一册。
       */
      const targetTextbookId = activeVolume?.id ?? '';
      /** 载荷构造见 `lib/dictationWrite.ts`（三个静默坑都在那里处理掉）。 */
      const values = {
        gradeCode: payload.gradeCode,
        semester: payload.semester,
        category: payload.category,
        note: payload.note,
        fromTextbook: payload.fromTextbook,
        ...(payload.lessonIds === undefined ? {} : { lessonIds: payload.lessonIds }),
      };
      if (kind === 'hanzi') {
        if (editing === null) {
          return adminApi.createHanzi(
            buildCreateHanziPayload({
              ...values,
              textbookId: targetTextbookId,
              charText: payload.text ?? '',
              pinyin: payload.pinyin,
            }),
          );
        }
        return adminApi.updateHanzi(
          editing.id,
          buildUpdateHanziPayload({ ...values, pinyin: payload.pinyin }),
        );
      }
      if (editing === null) {
        return adminApi.createWord(
          buildCreateWordPayload({
            ...values,
            textbookId: targetTextbookId,
            wordText: payload.text ?? '',
            pinyin: payload.pinyin,
          }),
        );
      }
      return adminApi.updateWord(
        editing.id,
        buildUpdateWordPayload({ ...values, pinyin: payload.pinyin }),
      );
    },
    onSuccess: async (data, vars) => {
      toast.showToast('已保存', 'success');
      setItemForm({ open: false, kind: 'hanzi', editing: null });
      setItemFormError(null);
      setItemLessonLinks(null);
      setItemLinksUnknown(false);
      /**
       * ---------- 多音字按音造词：把"这个字在这个词里读那个音"钉上 ----------
       *
       * 浮窗里每个读音下面都有「给这个音造词」：它记下 `{ 字, 读音 }` 再打开
       * **同一个**「新增词语」表单（不另造一套表单）。词保存成功之后，这里用
       * **既有**的 `PUT .../words/:id/readings` 把那个位置的读音写进去 ——
       * 否则新词里这个字会按"整词拼音"回落到默认读音，等于白选了那个音。
       *
       * ⚠️ 其它位置一律传空串（= 清除 → 从整词拼音推）：只钉这一个字，
       *    **不替用户猜**别的字的读音。
       * ⚠️ 这个字不在新词里（用户改了词面）就什么都不做 —— 不报错，
       *    因为"词保存好了"是主结果。
       */
      if (vars.kind === 'word' && pendingReading !== null) {
        const word = data as WordView;
        const chars = [...word.wordText];
        const readings = chars.map((ch) =>
          ch === pendingReading.charText ? pendingReading.reading : '',
        );
        if (chars.includes(pendingReading.charText)) {
          try {
            await adminApi.setWordCharReadings(word.id, readings);
            toast.showToast(
              `已录「${word.wordText}」，并把「${pendingReading.charText}」在这个词里标成 ${pendingReading.reading}`,
              'success',
            );
          } catch (err) {
            /**
             * 词已经存进去了 —— 所以这里**不是**"失败"，只说"读音没写上"，
             * 用户可以在浮窗里重新标。
             */
            toast.showToast(`词已保存，但读音没写上：${toErrorMessage(err)}`, 'error');
          }
        }
        setPendingReading(null);
      }
      await refreshAll();
    },
    onError: (err: unknown) => setItemFormError(toErrorMessage(err)),
  });

  /** 打开编辑：先把"这一条现在关联了哪些课文"查出来（列表响应里没有这个字段）。 */
  const openItemEdit = async (kind: ItemKind, item: HanziView | WordView) => {
    setItemFormError(null);
    setItemLinksUnknown(false);
    setItemLinksLoading(false);
    if (item.lessonIds !== null) {
      setItemLessonLinks(item.lessonIds);
      setItemForm({ open: true, kind, editing: item });
      return;
    }
    /**
     * ⚠️ 列表响应里没有 `lessonIds`，所以要单独查一次。查的**过程中**把
     *   `itemLinksLoading` 置起来：查完之前表单不显示勾选框、也不提交关联字段。
     *   否则"编辑一下备注"就会把关联好的课文**静默清空**（后端是整组替换）。
     */
    setItemLessonLinks(null);
    setItemLinksLoading(true);
    setItemForm({ open: true, kind, editing: item });
    try {
      const links = await adminApi.fetchLessonLinks(kind, item.id);
      /**
       * ⚠️ 词语那一支是"从列表里找自己那一行"得来的，列表可能被 `limit` 截断。
       *   截断时**不要**把这份可能不全的关联拿去整组替换（那等于删掉一部分关联）——
       *   按"查不到"处理，界面会说明并且这次保存不动关联。
       */
      if (links === null || links.truncated) {
        setItemLessonLinks(null);
        setItemLinksUnknown(true);
      } else {
        setItemLessonLinks(links.ids);
        setItemLinksUnknown(false);
      }
    } catch {
      // 查不到就**不动关联**（界面上会说明），绝不默认成空数组提交
      setItemLessonLinks(null);
      setItemLinksUnknown(true);
    } finally {
      setItemLinksLoading(false);
    }
  };

  // ---- 只改拼音（维护模式：这是**全站共享的字表数据**） ----
  const pinyinMutation = useMutation({
    mutationFn: async (vars: {
      kind: ItemKind;
      id: string;
      value: string;
      /**
       * 用户点开的那个位置（`hanzi_listing.id`）：那个位置若已有显式读音，
       * 跟着一起改（否则"改了拼音、外面的卡片没变"——2026-10-21 用户报的 bug）。
       * 词 / 没有位置的入口一律 `null`。
       */
      listingId?: string | null;
    }) =>
      vars.kind === 'hanzi'
        ? adminApi.setHanziPinyin(vars.id, vars.value, vars.listingId ?? null)
        : adminApi.setWordPinyin(vars.id, vars.value),
    onSuccess: async (_data, vars) => {
      toast.showToast(vars.value.trim() === '' ? '已恢复自动读音' : '读音已更新', 'success');
      setPinyinEdit(null);
      setPinyinError(null);
      /** 浮窗里那个"改拼音"框也要收起来（同一个 mutation 两个入口）。 */
      setReadingsPinyinOpen(false);
      /**
       * ⚠️ **必须重新拉列表**（用户要求"改完必须刷新列表，别等手动刷新"）。
       *    字卡的显示值是 `listingReading ?? pinyin` —— 服务端这一趟可能刚把
       *    某个位置的 `hanzi_listing.reading` 也改了，不重新拉就还是旧快照。
       */
      await refreshAll();
    },
    onError: (err: unknown) => setPinyinError(describeImportFailure(err)),
  });

  // ---- 标为多音字 / 标为单音字（**人工标注**，维护模式） ----
  //
  // ⚠️ 这与"这个字在拼音表里有几个读音"是两件事：技术层的多读音还在
  //    `readings` / `pinyinAlternates` 里（读音选择、按音造词、听写多音字专项都用它）。
  //    这里改的是**教学口径**的标注，默认 false = 单音字。
  const [polyphonicSavingId, setPolyphonicSavingId] = useState<string | null>(null);
  const polyphonicMutation = useMutation({
    mutationFn: async (vars: { id: string; polyphonic: boolean }) =>
      adminApi.setHanziPolyphonic(vars.id, vars.polyphonic),
    onMutate: (vars) => setPolyphonicSavingId(vars.id),
    onSuccess: async (_data, vars) => {
      toast.showToast(vars.polyphonic ? '已标为多音字' : '已标为单音字', 'success');
      await refreshAll();
    },
    onError: (err: unknown) => toast.showToast(toErrorMessage(err), 'error'),
    onSettled: () => setPolyphonicSavingId(null),
  });

  // ---------------------------------------------------------------- 渲染
  const textbooks = textbooksQuery.data?.textbooks ?? null;
  const textbooksMissing = !textbooksQuery.isLoading && textbooksQuery.data === undefined;
  /** 真的一条课本都没有（与"没拿到"是两件事）。 */
  const textbooksEmpty = textbooks !== null && textbooks.length === 0;
  const lessons = lessonsQuery.data?.lessons ?? null;
  const hanzi = hanziListQuery.data?.hanzi ?? null;
  const words = wordsListQuery.data?.words ?? null;

  /**
   * 浮窗里「这个字还出现在」：**当前这次筛选结果**里同一个字的其它位置。
   *
   * ⚠️ 只在同一屏内跳（用户明确要求）：数据就是当前列表结果（`hanzi`），不跨册、
   *    不跨筛选；字只出现一次 ⇒ 空数组 ⇒ 浮窗里那整块不渲染。
   */
  const hanziOccurrences = useMemo(
    () =>
      readingsOrigin !== 'browse' || readingsTarget === null || hanzi === null
        ? []
        : browseJumpCards(hanzi, readingsTarget, readingsCardKey),
    [readingsOrigin, readingsTarget, readingsCardKey, hanzi],
  );

  /**
   * 点浮窗里「这个字还出现在」的某一项 → **跳到那张卡**。
   *
   * 顺序与导入预览逐字相同（同一份 `useHanziJump`）：
   *   1. 先关浮窗：`Modal` 开着时 `body` 是 `overflow: hidden`，不关就滚不动；
   *   2. 滚过去 + 高亮 2 秒 + 焦点落到目标卡；
   *   3. **到此为止** —— ⛔ 不打开目标位置的浮窗（用户 2026-10-20 点名不要），
   *      用户看到 ring 之后自己点那张卡，浮窗才打开（与点卡那条路径一致）。
   */
  const jumpToHanziOccurrence = (key: string) => {
    if (hanzi === null) return;
    /** 键不在这份结果里（理论上不会发生）：不关窗、也不乱滚。 */
    if (!hanzi.some((item, index) => hanziRowKey(item, index) === key)) return;
    setReadingsTarget(null);
    /** 关了窗 ⇒ 那个"点开的位置"也作废（下一次开窗会重新记）。 */
    setReadingsListingId(null);
    setReadingsCardKey(null);
    setReadingsPinyinOpen(false);
    jump.jumpTo(key);
  };

  /**
   * ⚠️ **一条课本都没有时不再自动摊开弹窗**（2026-10-16 本轮用户要求）。
   *
   * 用户原话："请把『新建书本』的功能**默认收成**，用一个按钮来**找开**，点击后
   * 通过一个**浮动窗口**来录入。"
   *
   * 改造前这里有一个 effect：`textbooksEmpty` 时 `setNewTextbookOpen(true)` ——
   * 那是当时唯一的"可发现性"来源。现在**可发现性靠「＋ 添加出版社」**：
   * 0 本书时下拉里仍然有最后那一项（下拉这时**不禁用**，见
   * `lib/dictationBooksPicker.ts`），页面底部那张卡片上也点名了它，
   * 所以那个 effect 已删掉，`newTextbookOpen` 的初值就是 `false`。
   *
   * ⛔ 不要为了"帮用户省一次点击"把这个 effect 加回来：那正是用户点名要去掉的行为。
   */

  const currentHanzi = useMemo(() => {
    if (pinyinEdit?.kind !== 'hanzi' || hanzi === null) return null;
    return hanzi.find((item) => item.id === pinyinEdit.id) ?? null;
  }, [pinyinEdit, hanzi]);
  const currentWord = useMemo(() => {
    if (pinyinEdit?.kind !== 'word' || words === null) return null;
    return words.find((item) => item.id === pinyinEdit.id) ?? null;
  }, [pinyinEdit, words]);

  /**
   * 浏览 tab 副标题的前半句。
   *
   * ⚠️ 隐藏「课文」时**不再提"课文"**：副标题写着"按课文看"、界面里却没有
   *    课文下拉，等于教人去点一个不存在的东西。
   */
  const browseHintBase = lessonsUiEnabled() ? '按课本/年级/课文看' : '按课本/年级看';
  /**
   * Tab 清单与顺序**不在这个文件里定义**（见 `lib/dictationTabs.ts`，有单测）。
   *
   * 2026-10-18 用户要求：管理后台的三个标签改成
   * 「浏览与编辑 | 按书录入 | 整块导入（高级）」；紧接着又说
   * "把'整块导入（高级）'这个功能去掉" —— 于是现在维护模式只有**两个**标签，
   * 而整块导入只是**不渲染**（`DICTATION_WHOLE_IMPORT_UI_ENABLED=false`，
   * 后端端点与组件一行都没删）。
   *
   * ⚠️ 默认页是 `DEFAULT_DICTATION_TAB`（按书录入），**不随顺序变**。
   */
  const tabs = dictationTabs({ maintain: isMaintain, browseHint: browseHintBase });

  /**
   * ---------- 站点还没有任何字表（家长模式）----------
   *
   * ⚠️ 这里**整页**给人话空状态，而不是继续渲染下面的 tab 与面板：
   *    一本课本都没有时，下面是一张"课本下拉是空的、到处是「—」"的界面 ——
   *    家长看到的会是"这个功能坏了"，而不是"这件事不归我管、等管理员录"。
   *    这一条必须与"没拿到数据"（`textbooks === null`）分开说。
   *
   * ⚠️ 管理员模式**不**走这条：他要看到的是"去建第一本课本"的引导
   *    （见下面的引导卡片）。
   */
  if (!isMaintain && textbooksEmpty) {
    return (
      <EmptyState
        icon="📚"
        title="站点还没有录入字表"
        description="这套字表由站点管理员统一维护，家长这边不需要录入。等管理员把课本、生字、词语录进来之后，这里就能按书查看、按表选词了。"
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* ---------- Tab ---------- */}
      <div role="tablist" aria-label="语文听写" className="flex flex-wrap gap-2">
        {tabs.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={tab === item.key}
            onClick={() => setTab(item.key)}
            className={
              tab === item.key
                ? 'rounded-xl bg-brand-600 px-3 py-2 text-left text-sm font-semibold text-white'
                : 'rounded-xl border border-slate-300 bg-white px-3 py-2 text-left text-sm font-medium text-slate-700 hover:bg-slate-50'
            }
          >
            <span className="block">{item.label}</span>
            <span className={tab === item.key ? 'block text-[11px] text-brand-100' : 'block text-[11px] text-slate-400'}>
              {item.hint}
            </span>
          </button>
        ))}
      </div>

      {/* ---------- 课本列表本身拿不到时（不是"还没有课本"） ---------- */}
      {textbooksQuery.isError && (
        <ErrorState
          message={`没拿到课本列表：${toErrorMessage(textbooksQuery.error)}`}
          onRetry={() => void textbooksQuery.refetch()}
          compact
        />
      )}

      {/*
        ---------- 站点还没有任何字表 ----------
        ⚠️ 家长模式在**上面**就整页返回了（人话空状态），所以下面那张
           "去建第一本课本"的引导卡片只对管理员出现。
      */}

      {/* ============================================ 按书看 / 录（第五步的主视角） */}
      {/*
        ⚠️ 2026-10-18（本轮修正）：`items` 这一组四件套**又回来了**，但**只给家长**传 ——
           维护模式的 ② 仍然不渲染（用户："管理员端录入字词的那一块不需要再显示字词"），
           所以也不为他发那次请求（见 `booksItemsQuery` 的 `enabled`）。
      */}
      {tab === 'books' && (
        <DictationBooksPanel
          textbooks={textbooks}
          textbooksMissing={textbooksMissing}
          loading={textbooksQuery.isLoading}
          canEdit={isMaintain}
          selection={booksSelection}
          onSelect={changeBooksSelection}
          counts={booksCounts}
          {...(!isMaintain
            ? {
                itemsHanzi: booksItemsQuery.data?.hanzi ?? null,
                itemsWords: booksItemsQuery.data?.words ?? null,
                itemsTotal: booksItemsQuery.data?.total ?? null,
                itemsLoading: booksItemsQuery.isLoading,
                itemsError: booksItemsQuery.isError
                  ? toErrorMessage(booksItemsQuery.error)
                  : null,
                /** 书还没建过 ⇒ **没得查**（面板说"这一册还没有录入"，不是报错）。 */
                itemsEnabled: booksQueryComplete && activeBooksVolume !== null,
                onRetryItems: () => void booksItemsQuery.refetch(),
              }
            : {})}
          {...(isMaintain
            ? {
                text: listText,
                /**
                 * ⚠️ 打开「添加出版社」浮窗（入口是出版社下拉的最后一项）。
                 *    浮窗里**只有出版社名**，年级 / 学期用页面那两格；建出来的
                 *    那一册 = 出版社 + 当前选中的年级 + 学期。
                 * ⚠️ 浮窗渲染在**这一屏之外**（见本组件末尾那段 `isMaintain` 的弹窗）。
                 * ⛔ 页面上那块「＋ 新建课本（出版社 + 年级 + 学期）」已按用户要求删除，
                 *    不要再加一个就地展开的表单回来。
                 */
                onAddPublisher: openAddPublisher,
                onText: (value: string) => {
                  /** ⚠️ 改内容就**作废预览**（提交必须用看过的那一份）。 */
                  setListText(value);
                  /** 改文本 ⇒ 行号/位置全变了，旧的选音必须作废。 */
                  setListReadings({});
                  setListPreview(null);
                  setListCommit(null);
                  setListPreviewError(null);
                  setListPreviewIssues([]);
                  setListCommitError(null);
                },
                preview: listPreview,
                previewError: listPreviewError,
                previewIssues: listPreviewIssues,
                previewing: listBusy === 'preview',
                commit: listCommit,
                commitError: listCommitError,
                committing: listBusy === 'commit',
                onPreview: () => void runListPreview(),
                onCommit: () => void runListCommit(),
                onReset: () => {
                  setListText('');
                  setListReadings({});
                  setListPreview(null);
                  setListCommit(null);
                  setListPreviewError(null);
                  setListPreviewIssues([]);
                  setListCommitError(null);
                },
                /** 点重复出现的字 → 选这个位置的读音（迁移 0018）。 */
                onPickReading: pickListReading,
                readingsBusy: listBusy === 'preview',
              }
            : {})}
          onOpenHanzi={(item) => {
            /** 同上：用户自己点了卡 ⇒ 取消还在路上的跳转。 */
            jump.cancel();
            /** 家长端「按书看字表」：不跨屏跳转。 */
            setReadingsOrigin('books');
            setReadingsTarget(item);
            /**
             * ⚠️ 家长端只读（`isMaintain === false`）⇒ 没有写入口，也就没有"位置"。
             *    明确清掉，别把上一屏管理员点的那张卡的位置带进来。
             */
            setReadingsListingId(null);
            /** 家长端没有跳转列表，但别把上一屏的键留在这里。 */
            setReadingsCardKey(null);
          }}
        />
      )}

      {/*
        ============================================ 整块导入（通用入口，**只维护模式**）

        ⚠️ 2026-10-18 本轮：用户要求"把'整块导入（高级）'这个功能去掉"。
           做法是**开关式隐藏**（`DICTATION_WHOLE_IMPORT_UI_ENABLED=false`，
           见 `lib/dictationTabs.ts`）：tab 不再渲染，这段面板也跟着不渲染；
           **组件与后端端点一行都没删** —— 恢复只改那个常量。
        ⚠️ 这里再判一次 `wholeImportUiEnabled()`（不只是靠 tab 不存在）：
           万一将来有人把 tab 加回来却忘了这一处，也不会渲染出一块没人能到达的面板。
      */}
      {isMaintain && wholeImportUiEnabled() && tab === 'import' && (
        <DictationImportPanel
          state={importState}
          textbooks={textbooks}
          busy={importBusy}
          onInput={(patch) => setImportState((prev) => setImportInput(prev, patch))}
          onPreview={() => void runPreview()}
          onCommit={() => void runCommit()}
          onReset={() => setImportState(resetImport(importState))}
          onRestoreLastText={() => setImportState((prev) => restoreLastCommittedText(prev))}
        />
      )}

      {/* ============================================ 浏览（+ 编辑） */}
      {tab === 'browse' && (
        <>
          {/*
            ⚠️ 这里**故意不再放一层骨架屏**：各个列表卡片自己已经有 loading 态
               （`DictationItemsPanel` 的 `loading`），上面再叠一层会出现两条灰条同时闪，
               而且下面那张真实的卡已经渲染出来了 —— 看起来像"页面抖了一下"。
          */}
          {/*
            ⚠️ 课文界面隐藏时（`DICTATION_LESSONS_UI_ENABLED=false`），课文列表的失败
               **不**报在这里：那张卡片已经不渲染了，报一句"有一份列表没拿到"
               只会让人去找一个看不见的东西。
          */}
          {((lessonsUiEnabled() && lessonsQuery.isError) ||
            (browseBlock.hanzi && hanziListQuery.isError) ||
            (browseBlock.words && wordsListQuery.isError)) && (
            <ErrorState
              compact
              message={`有一份列表没拿到：${toErrorMessage(
                (browseBlock.hanzi ? hanziListQuery.error : null) ??
                  (browseBlock.words ? wordsListQuery.error : null) ??
                  lessonsQuery.error,
              )}`}
              onRetry={() => {
                void lessonsQuery.refetch();
                if (browseBlock.hanzi) void hanziListQuery.refetch();
                if (browseBlock.words) void wordsListQuery.refetch();
              }}
            />
          )}
          {/*
            「上次记住的筛选里有项失效了」—— 中性色的一句话，**不是错误态**：
            数据变了不是用户做错了什么，而且已经安全回落到「全部」。
          */}
          {filterNotice !== null && (
            <p className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
              {filterNotice}
            </p>
          )}
          <DictationItemsPanel
            textbooks={textbooks}
            textbooksMissing={textbooksMissing}
            lessons={lessons}
            lessonsTotal={lessonsQuery.data?.total ?? null}
            lessonsMissing={lessonsQuery.data !== undefined && lessons === null}
            hanzi={hanzi}
            hanziTotal={hanziListQuery.data?.total ?? null}
            hanziMissing={hanziListQuery.data !== undefined && hanzi === null}
            words={words}
            wordsTotal={wordsListQuery.data?.total ?? null}
            wordsMissing={wordsListQuery.data !== undefined && words === null}
            loading={
              lessonsQuery.isLoading ||
              /**
               * 选过了、但还在等课本列表校对（恢复的课本可能已经被删了）：
               * 这一小会儿**不要**说"没拿到这份数据"——那会闪一句假话。
               */
              (filterActive && !listsEnabled) ||
              /**
               * ⚠️ 只等**当前渲染的那一块**：选识字表时词语块不渲染/不查询，
               *    把它的 loading 算进来会让字块一直显示骨架屏（永远等不到）。
               */
              (hanziListEnabled && hanziListQuery.isLoading) ||
              (wordsListEnabled && wordsListQuery.isLoading)
            }
            canEdit={isMaintain}
            filter={draft}
            appliedFilter={filter}
            onFilter={(patch) => {
              /**
               * ⚠️ 只改**待生效**那套（`draft`）：不发请求、下面的列表保持上一次的
               *    结果不动（用户原话"不要选一个条件就刷新一个"）。
               *    升格成已生效只有两个入口：`applyQuery`（点查询 / 回车）与
               *    恢复出来的那一套（初值）。
               */
              setFilterNotice(null);
              setDraft((prev) => ({ ...prev, ...patch }));
            }}
            onQuery={applyQuery}
            onResetFilter={resetDraftFilter}
            pinyinEdit={pinyinEdit}
            pinyinSaving={pinyinMutation.isPending}
            pinyinError={pinyinError}
            onStartPinyin={(kind, itemId) => {
              setPinyinError(null);
              setPinyinEdit({ kind, id: itemId });
            }}
            onSavePinyin={(kind, item, value) => {
              setPinyinError(null);
              /**
               * ⚠️ 列表里就地改拼音现在只剩**词**这一支（字卡上的拼音编辑 2026-10-18
               *    已搬进浮窗）。字那一支仍然把 `listingId` 带上：万一将来有人把
               *    就地编辑加回字卡，也不会又踩"改了外面不变"那个坑。
               */
              pinyinMutation.mutate({
                kind,
                id: item.id,
                value,
                listingId: 'listingId' in item ? (item.listingId ?? null) : null,
              });
            }}
            onCancelPinyin={() => {
              setPinyinEdit(null);
              setPinyinError(null);
            }}
            /**
             * ⚠️ 「标为多音字 / 标为单音字」**不再挂在字卡上**（2026-10-18 本轮）：
             *    用户要求"所有的操作都在这个框框（点开的浮窗）里面进行"，
             *    所以那个按钮搬进了 `HanziReadingsModal`，mutation 仍是同一个
             *    （`polyphonicMutation`）。这里只把"打开浮窗"接上。
             */
            onOpenHanzi={(item, cardKey) => {
              /** 用户自己点了卡 ⇒ 取消还在路上的跳转。 */
              jump.cancel();
              setReadingsOrigin('browse');
              setReadingsTarget(item);
              /**
               * ⚠️ 记下**这张卡是哪个位置**（`hanzi_listing.id`）：浮窗里改拼音时
               *    要把它一起发给服务端 —— 否则那个位置的显式读音不会被改，
               *    关掉浮窗后卡片还是旧值（2026-10-21 用户报的 bug）。
               */
              setReadingsListingId(item.listingId ?? null);
              /**
               * ⚠️ 同时记下**这张卡自己的跳转键**（与 `DictationItemsPanel` 登记的
               *    那个键逐字相同）。浮窗的数据一刷新就换成「读音汇总」DTO（没有
               *    `listingId`），只靠 `item` 算当前键会把"自己"也算成"别的位置"。
               */
              setReadingsCardKey(cardKey ?? null);
            }}
            /* ---------- 同一个字互相跳转（与导入预览同一份实现） ---------- */
            registerCard={jump.register}
            highlightedKey={jump.activeKey}
            onCreateLesson={() => {
              setLessonFormError(null);
              setLessonForm({ open: true, editing: null });
            }}
            onEditLesson={(lesson) => {
              setLessonFormError(null);
              setLessonForm({ open: true, editing: lesson });
            }}
            onDeleteLesson={(lesson) =>
              setDeleteTarget({
                kind: 'lesson',
                id: lesson.id,
                label: `${lesson.lessonLabel} ${lesson.title}`.trim(),
                detail:
                  '删掉这节课文之后，它关联的「字 ↔ 课」「词 ↔ 课」关系会一起清掉；' +
                  '字与词本身会保留（别的课还在用它们）。',
              })
            }
            onCreateHanzi={() => {
              setItemFormError(null);
              setItemLessonLinks(null);
              setItemLinksUnknown(false);
              setItemForm({ open: true, kind: 'hanzi', editing: null });
            }}
            onCreateWord={() => {
              setItemFormError(null);
              setItemLessonLinks(null);
              setItemLinksUnknown(false);
              setItemForm({ open: true, kind: 'word', editing: null });
            }}
            onEditItem={(kind, item) => void openItemEdit(kind, item)}
            onDeleteItem={(kind, item) =>
              setDeleteTarget({
                kind,
                id: item.id,
                label: kind === 'hanzi' ? (item as HanziView).charText : (item as WordView).wordText,
                detail:
                  '删掉这一条之后，课本里就没有它了；' +
                  (kind === 'hanzi'
                    ? '由它组成的词不会跟着删（那些词还在词表里）。'
                    : '它和各个字的关联会一起清掉（字本身保留）。') +
                  ' 人工改过的拼音也会一起丢掉。',
              })
            }
          />

          {/* 拼音编辑框：编辑在列表行里就地展开，这里只处理"正在编辑的那一条"的保存语义 */}
          {isMaintain && pinyinEdit !== null && currentHanzi === null && currentWord === null && (
            <p className="text-xs text-slate-500">
              （正在编辑的这一条不在当前筛选结果里了 —— 保存后可能看不到它，换个筛选条件即可。）
            </p>
          )}
        </>
      )}

      {/* ============================================ 选词预览（**只有家长模式**） */}
      {canPick && tab === 'pick' && (
        <DictationPickPanel
          textbooks={textbooks}
          /*
            ⚠️ 界面上**只**显示"年级"：这里是选词预览默认选中的年级，来自内部选定的孩子
              （确定性规则见 `lib/dictationAnchor.ts`）。家长看到的是年级选择器本身，
              不需要知道这个默认值是从哪个孩子身上取的。
          */
          defaultGradeCode={studentGradeCode}
          applied={pickResult?.applied ?? null}
          words={pickResult?.words ?? null}
          total={pickResult?.total ?? null}
          wrongTotal={pickResult?.wrongTotal ?? null}
          note={pickResult?.note ?? null}
          /*
            错词本的数量：给"取词来源"那个下拉当提示用（"错词本里还有 7 个没掌握"）。
            ⚠️ `null` = **没拿到**，界面上会说"数量没拿到"而不是"错词本是空的"。
          */
          openMistakeCount={mistakeSummary?.openCount ?? null}
          dueMistakeCount={mistakeSummary?.dueCount ?? null}
          missing={pickResult?.missing ?? false}
          loading={pickMutation.isPending}
          error={pickNotice}
          notReadyMessage={null}
          /*
            「只听某个读音」的可选读音：从**多音字**里取（最多 6 个）。
            ⚠️ 以前从「浏览」列表里取，而那个列表现在未选筛选就不请求了 ——
               所以改成自己的小查询（见上面的 `readingOptionsQuery`）。
            ⚠️ `null` = **没拿到**（界面会说"读音选项没拿到"），`[]` = 真没有多音字。
          */
          readingOptions={readingOptions}
          onPick={(payload) => {
            pickMutation.reset();
            setPickNotice(null);
            pickMutation.mutate(payload);
          }}
        />
      )}

      {/* ============================================ 弹窗 */}
      {/*
        ⚠️ **维护模式的弹窗在浏览模式下整个不渲染**（不是"打开也是空的"）：
           它们的存在本身就会把"编辑"这个概念带进家长端。
             · 读音与造词浮层 —— **读**，两种模式都要（写入口只在维护模式渲染）；
             · 课文表单 / 字词表单 / 删除确认 —— 写，只有管理员要。
        ⚠️ 2026-10-18 本轮：原来的「组词」浮层（`HanziWordsModal`）**不再从这里打开** ——
           用户要求"所有的操作都在这个框框（点字卡弹出的浮窗）里面进行"，
           而浮窗里已经按读音列出了每个音造过的词。组件本身没删（别的入口可能复用）。
      */}
      <HanziReadingsModal
        hanzi={readingsTarget}
        /* ---------- 同一个字在当前筛选结果里的其它位置（可点跳转） ---------- */
        occurrences={hanziOccurrences}
        onJumpToOccurrence={jumpToHanziOccurrence}
        data={readingsQuery.data ?? null}
        loading={readingsQuery.isLoading}
        error={readingsQuery.isError ? toErrorMessage(readingsQuery.error) : null}
        /*
          ⚠️ 只用来把收录里的 `textbookId` 翻成「人民教育出版社 · 小学一年级 · 上册」
             （弹框里那一格要说清"在哪本书的哪张表上"）。
             两种模式都传：家长端也**必须**看得到这个字在哪张表上。
        */
        textbooks={textbooks}
        onRetry={() => void readingsQuery.refetch()}
        onClose={() => {
          setReadingsTarget(null);
          setReadingsListingId(null);
          setReadingsCardKey(null);
          setReadingsPinyinOpen(false);
        }}
        /* ---------- 写入口：只有维护模式传（家长端一个按钮都不渲染） ---------- */
        canEdit={isMaintain}
        polyphonicSaving={readingsTarget !== null && polyphonicSavingId === readingsTarget.id}
        onTogglePolyphonic={
          isMaintain && readingsTarget !== null
            ? () =>
                polyphonicMutation.mutate({
                  id: readingsTarget.id,
                  polyphonic: !readingsTarget.polyphonic,
                })
            : undefined
        }
        pinyinOpen={readingsPinyinOpen}
        onStartPinyin={
          isMaintain
            ? () => {
                setPinyinError(null);
                setReadingsPinyinOpen(true);
              }
            : undefined
        }
        pinyinSaving={pinyinMutation.isPending}
        pinyinError={pinyinError}
        /**
         * ⚠️ 从字卡点开（有 `listingId`）时才说"这张卡也会一起改"：句子里那句
         *    "别的、单独标过音的位置不动"正是用户第一次踩的那个坑的另一半。
         */
        pinyinAffectsPosition={readingsListingId !== null}
        onSavePinyin={
          isMaintain && readingsTarget !== null
            ? (value) => {
                setPinyinError(null);
                /**
                 * ⚠️ 把**用户点开的那个位置**一起发过去（`readingsListingId`，开窗时
                 *    记下的那张卡）。服务端会把它那一行显式 `reading` 一起改掉 ——
                 *    否则字卡优先显示位置读音，用户改完还是看到旧的（2026-10-21 bug）。
                 */
                pinyinMutation.mutate({
                  kind: 'hanzi',
                  id: readingsTarget.id,
                  value,
                  listingId: readingsListingId,
                });
              }
            : undefined
        }
        onCancelPinyin={
          isMaintain
            ? () => {
                setReadingsPinyinOpen(false);
                setPinyinError(null);
              }
            : undefined
        }
        onEditHanzi={
          isMaintain && readingsTarget !== null
            ? () => {
                const item = readingsTarget;
                setReadingsTarget(null);
                setReadingsListingId(null);
                setReadingsCardKey(null);
                setReadingsPinyinOpen(false);
                void openItemEdit('hanzi', item);
              }
            : undefined
        }
        onDeleteHanzi={
          isMaintain && readingsTarget !== null
            ? () => {
                const item = readingsTarget;
                setReadingsTarget(null);
                setReadingsListingId(null);
                setReadingsCardKey(null);
                setReadingsPinyinOpen(false);
                setDeleteTarget({
                  kind: 'hanzi',
                  id: item.id,
                  label: item.charText,
                  detail:
                    '删掉这个字之后，课本里就没有它了；由它组成的词不会跟着删（那些词还在词表里）。' +
                    ' 人工改过的拼音也会一起丢掉。',
                });
              }
            : undefined
        }
        /* 多音字按音造词：记下"哪个音"，再打开既有的「新增词语」表单。 */
        onCreateWord={
          isMaintain
            ? (reading) => {
                if (readingsTarget === null) return;
                setPendingReading({ charText: readingsTarget.charText, reading });
                setReadingsTarget(null);
                setReadingsListingId(null);
                setReadingsCardKey(null);
                setReadingsPinyinOpen(false);
                setItemFormError(null);
                setItemLessonLinks(null);
                setItemLinksUnknown(false);
                setItemForm({ open: true, kind: 'word', editing: null });
              }
            : undefined
        }
        /**
         * **一次造多个词**（2026-10-30 用户要求）。
         *
         * 与上面的单个造词走**不同**的窗口：那个是通用「新增词语」表单（还能选课本、
         * 年级、课时），这个只做"一个读音下批量造词"这一件事 —— 所以不共用，
         * 也就不会把通用表单越改越复杂。
         *
         * 打开时把这个字记进 `batchWordTarget`（窗口关掉后仍然需要它：
         * 课本 / 年级 / 学期 / 字面都从它来）。保存完由 `onSaved` 刷新列表。
         */
        onBatchCreateWords={
          isMaintain
            ? (reading) => {
                if (readingsTarget === null) return;
                setBatchWordTarget({ hanzi: readingsTarget, reading });
              }
            : undefined
        }
        /** **选词造词**（2026-11-04）：从词库里挑现成的词。 */
        onPickWords={
          isMaintain
            ? (reading) => {
                if (readingsTarget === null) return;
                setPickWordTarget({ hanzi: readingsTarget, reading });
              }
            : undefined
        }
        /**
         * **删除自造词**（2026-11-04 用户要求："造的词要能删除"）。
         *
         * 删的是 `word` 那一行（`word_hanzi` 关联由外键级联清掉）；
         * 课本词在界面上没有这个按钮，服务层也不会被前端绕过 —— 但这里不做额外判断，
         * 以服务端为准（只读模式 `isMaintain === false` 时不传，按钮根本不渲染）。
         */
        onDeleteWord={
          isMaintain
            ? async (wordId, wordText) => {
                try {
                  await adminApi.deleteWord(wordId);
                  toast.showToast('已删除「' + wordText + '」', 'success');
                  await refreshAll();
                } catch (err) {
                  toast.showToast(toErrorMessage(err), 'error');
                }
              }
            : undefined
        }
        /**
         * **改自造词的字**（2026-10-30）。课本词表由服务端拒（400），这里不重复判。
         * 成功后 `refreshAll()`：浮窗里的词面、拼音、字↔词关联都会跟着变。
         */
        onRenameWord={
          isMaintain
            ? async (wordId, wordText) => {
                try {
                  await adminApi.updateWord(wordId, { wordText });
                  toast.showToast('已改好', 'success');
                  await refreshAll();
                } catch (err) {
                  toast.showToast(toErrorMessage(err), 'error');
                }
              }
            : undefined
        }
        /* ---------- 隐藏不常用的音（受控；**服务端是唯一来源**，见上面的注释） ---------- */
        hiddenReadings={hiddenReadings}
        /**
         * ⚠️ **只有维护模式接这个回调**：家长端 `undefined` ⇒ 浮窗里
         *    「隐藏这个音 / 显示」两个按钮**不渲染**（不是灰掉）。
         *    他仍然看得到 `hiddenReadings`（「已隐藏的读音（N）」），
         *    所以"管理员藏了哪些"在家长端是**可见**的 —— 这正是用户要的口径。
         */
        onToggleHiddenReading={isMaintain ? toggleHiddenReadingOnServer : undefined}
      />

      {/* ---------- 选词造词（2026-11-04）：从词库挑现成的词 ---------- */}
      <WordCandidatePicker
        open={pickWordTarget !== null}
        hanzi={pickWordTarget?.hanzi ?? null}
        reading={pickWordTarget?.reading ?? ''}
        onClose={() => setPickWordTarget(null)}
        onSaved={refreshAll}
      />

      {/* ---------- 一次造多个词（2026-10-30） ---------- */}
      <WordBatchCreateModal
        open={batchWordTarget !== null}
        hanzi={batchWordTarget?.hanzi ?? null}
        reading={batchWordTarget?.reading ?? ''}
        onClose={() => setBatchWordTarget(null)}
        onSaved={refreshAll}
      />

      {isMaintain && (
        <>
          {/*
            ---------- 「添加出版社」浮窗 ----------
            ⚠️ 渲染位置在**面板之外**（这里是页面级的弹窗区）：`DictationBooksPanel`
               只负责在下拉最后一项放「＋ 添加出版社」，点开的是这一个 `Modal`。
            ⚠️ 浮窗里**只有出版社名一格**（用户定稿）：年级 / 学期用页面那两格，
               提交时由这里补进 `NewTextbookInput`（三格齐了才是一册）。
            ⚠️ **只有维护模式渲染**（家长端连这个组件都不出现 —— 建册是管理员的事；
               源码守卫测试 `lib/dictationAnchor.test.ts` 守着家长页面那几个字符串）。
            ⚠️ 关闭（`Escape` / 点遮罩 / ✕ / 取消）**不保留已填内容**，理由见
               `DictationNewTextbookForm` 的文件头。
          */}
          <DictationNewTextbookForm
            open={newTextbookOpen}
            textbooks={textbooks}
            /** 只有下拉末项这一个入口 ⇒ 永远从空白开始（不再有"预填当前出版社"）。 */
            initialPublisher=""
            volumeLabel={describeSelectedVolume(booksSelection.gradeCode, booksSelection.semester)}
            submitting={newTextbookMutation.isPending}
            error={newTextbookError}
            onCancel={() => {
              setNewTextbookOpen(false);
              setNewTextbookError(null);
            }}
            onSubmit={(publisher) =>
              newTextbookMutation.mutate({
                publisher,
                name: '',
                /** 页面那两格就是"建在哪一册"；空着才用默认值兜底（正常路径不会空）。 */
                gradeCode: booksSelection.gradeCode === '' ? DEFAULT_BOOKS_GRADE : booksSelection.gradeCode,
                semester: booksSelection.semester === '' ? DEFAULT_BOOKS_SEMESTER : booksSelection.semester,
              })
            }
          />

          {/*
            「新建 / 编辑课文」弹窗：**暂时隐藏**（`DICTATION_LESSONS_UI_ENABLED`）。
            它唯一的两个打开入口都在「课本与课文」那张卡里，而那张卡现在不渲染；
            这里再挡一道，保证"开关关掉 ⇒ 这个弹窗不可能出现"。
          */}
          {lessonsUiEnabled() && (
            <DictationLessonForm
              open={lessonForm.open}
              editing={lessonForm.editing}
              textbooks={textbooks}
              /*
                ⚠️ 2026-10-18：筛选里没有 `textbookId` 了 —— 用「出版社 + 年级 + 学期」
                   解析出的那一册（解析不到就是空串，表单会说明"还没确定写进哪一册"）。
              */
              defaultTextbookId={activeVolume?.id ?? ''}
              submitting={lessonMutation.isPending}
              error={lessonFormError}
              onClose={() => {
                setLessonForm({ open: false, editing: null });
                setLessonFormError(null);
              }}
              onSubmit={(payload) => lessonMutation.mutate({ editing: lessonForm.editing, payload })}
            />
          )}

          <DictationItemForm
            open={itemForm.open}
            kind={itemForm.kind}
            editing={itemForm.editing}
            /*
              ⚠️ 新增时写进哪一册 = 「出版社 + 年级 + 学期」解析出的那一册
                 （与 `itemMutation` 的落点、面板上按钮的启用判据是**同一个** `activeVolume`）。
            */
            textbookId={activeVolume?.id ?? ''}
            lessons={lessons}
            submitting={itemMutation.isPending}
            error={itemFormError}
            lessonLinksUnknown={itemLinksUnknown && itemForm.editing !== null}
            lessonLinksLoading={itemLinksLoading}
            onClose={() => {
              setItemForm({ open: false, kind: 'hanzi', editing: null });
              setItemFormError(null);
              setItemLessonLinks(null);
              setItemLinksLoading(false);
              setItemLinksUnknown(false);
              /**
               * ⚠️ 取消这次「给这个音造词」时：
               *
               * 1. **回到上一层**（2026-11-03 用户报的：
               *    "点'取消'后，没有返回上一层，而是弹窗消失了"）——
               *    这个表单是从「读音与造词」浮窗里点「给这个音造词」进来的，
               *    取消就该退回那个浮窗，而不是直接把人扔回列表；
               * 2. 记下的读音**必须清掉**：留着它，下一次从别处新增词语会
               *    **莫名其妙**被钉上一个读音。
               */
              const back = pendingReading;
              setPendingReading(null);
              if (back !== null) {
                const target = (hanzi ?? []).find((item) => item.charText === back.charText) ?? null;
                if (target !== null) {
                  setReadingsOrigin('browse');
                  setReadingsTarget(target);
                }
              }
            }}
            onSubmit={(payload) =>
              itemMutation.mutate({
                kind: itemForm.kind,
                editing: itemForm.editing,
                /**
                 * ⚠️ 编辑时把**页面查到的那份关联**覆盖回去：表单拿到的 `editing` 是列表项，
                 *   它上面**没有** `lessonIds`（`null`）。如果查到了就按"整组替换"提交；
                 *   没查到/还没查完，表单自己也不会带这个字段（见 `DictationItemForm`），
                 *   这里同样不要补空的 —— 否则关联会被清空。
                 */
                payload:
                  itemForm.editing !== null && itemLessonLinks !== null
                    ? { ...payload, lessonIds: itemLessonLinks }
                    : payload,
              })
            }
          />

          <DeleteConfirmModal
            target={deleteTarget}
            deleting={deleteMutation.isPending}
            error={deleteError}
            onCancel={() => {
              setDeleteTarget(null);
              setDeleteError(null);
            }}
            onConfirm={() => {
              if (deleteTarget !== null) deleteMutation.mutate(deleteTarget);
            }}
          />
        </>
      )}

      {/* 页脚骨架：只在"课本列表还没到"时出现（避免大片空白） */}
      {textbooksQuery.isLoading && textbooks === null && (
        <div className="space-y-2">
          <Skeleton className="h-24 w-full" />
        </div>
      )}

      {/* 维护模式下一条课本都没有时的引导（家长那边由上面的空状态负责） */}
      {isMaintain && !textbooksQuery.isLoading && textbooksEmpty && (
        <Card className="bg-amber-50/60">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-semibold text-slate-900">还没有任何出版社</p>
              {/*
                ⚠️ 空状态必须指向「＋ 添加出版社」（下拉最后一项 + 下面这个按钮）：
                    旧文案指的是已删除的「＋ 新建课本（出版社 + 年级 + 学期）」那块，
                    再提它就是叫人去找一个不存在的东西。
                ⛔ 也不要再提「整块导入」：那个前端入口默认隐藏（`wholeImportUiEnabled()`）。
                ⚠️ 「还没有任何出版社」这句**保留**（用户确认）：确实要先有出版社；
                    但**不要**说"建第一册"—— 三格选好后录三张表，保存时会自动建这一册。
              */}
              <p className="mt-0.5 text-sm text-slate-600">
                这个部署的字表还是空的。先在下面选好年级和学期，点「＋ 添加出版社」把这家出版社
                登记进来；之后三格选好就能在「按书录入」里录识字表 / 写字表 / 词语表
                （保存时会自动归到这本书上）。
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              <Button
                onClick={() => {
                  /** 浮窗在「按书录入」那一屏之外：先把人送到那一屏，再打开它。 */
                  setTab('books');
                  openAddPublisher();
                }}
              >
                ＋ 添加出版社
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* 维护模式下的页脚：管理员该知道自己在改**站点**数据 */}
      {isMaintain && (
        <p className="text-xs text-slate-500">
          这里改的是<strong>站点共享</strong>的字表：所有家庭的听写都用这一份。
          家长能看、能选词、能布置听写，但改不了 —— 多音字的读音也在这一层统一纠。
        </p>
      )}
    </div>
  );
}

/**
 * 把失败变成一句给人看的话。
 *
 * ⚠️ 这里**必须**走 `describeApiFailure`：项目的 `ApiError` 已经带中文 message，
 * 但**后端的 zod 漏成 500** 时 message 是一串 JSON（生产实测见
 * `real-dictation-responses-2026-10-14.ts` 的 `ZOD_500_*` 夹具），
 * 直接把 JSON 糊在界面上等于没处理。
 */
function describeImportFailure(error: unknown): string {
  if (error === null || error === undefined) return '';
  return dictationApi.describeApiFailure(error).message;
}

/**
 * 失败时**逐条**的具体原因（行号 + 词元 + 原文 + 原因）—— 与上面那句一起用。
 *
 * ⚠️ 为什么两件事都要（2026-10-16 用户实测）：后端 `AppError` 的 `message` 是
 *    **按错误码给的通用文案**（"提交的内容有误，请检查后重试"），真正的原因在
 *    `details` 里。`describeImportFailure` 负责"一句话说清最要紧的那条"，
 *    这里负责"**全部**列出来"（界面用 `ImportIssueList` 渲染，可滚动 / 可折叠）。
 *    改造前这条通道被整块丢掉 —— 那正是"笼统错误盖掉具体原因"的根因。
 */
function describeImportFailureDetails(error: unknown): ImportIssueRow[] {
  if (error === null || error === undefined) return [];
  return dictationApi.describeApiFailure(error).details;
}
